// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

/// <reference lib="dom" />

import { z } from 'zod';
import type {
    CloverOAuthAuthorizationStatus,
    StartCloverOAuthReauthorizationResponse,
} from './types/ipg/clover/clover-oauth.types';
import {
    CloverOAuthAuthorizationStatusSchema,
    StartCloverOAuthReauthorizationRequestSchema,
    StartCloverOAuthReauthorizationResponseSchema,
} from './schemas/ipg/clover/clover-oauth.schemas';
import {
    ApiError,
    createPillarApiRequestExecutor,
    dedupe,
    type ApiValidationError,
    type HttpRequestHeaders,
    type PillarApiClientOptions,
    type PillarApiRequestOptions,
} from './pillar-api-core';
export { ApiError, dedupe } from './pillar-api-core';
export type {
    ApiValidationError,
    FetchImplementation,
    FetchRequestInit,
    HttpRequestHeaders,
    PillarApiClientOptions,
    PillarApiRequestOptions,
} from './pillar-api-core';
import type { CloverReadinessResult } from './types/ipg/clover/clover-readiness.types';
import { CloverReadinessResultSchema } from './schemas/ipg/clover/clover-readiness.schemas';
import type { ProcessCloverCardNotPresentPaymentRequest } from './types/ipg/clover/clover-card-not-present-payment.types';
import { ProcessCloverCardNotPresentPaymentRequestSchema } from './schemas/ipg/clover/clover-card-not-present-payment.schemas';

export interface CloverWebhookProcessingResult {
    readonly attempted: number;
    readonly processed: number;
    readonly failed: number;
}

const CloverWebhookProcessingResultSchema: z.ZodType<CloverWebhookProcessingResult> = z
    .object({
        attempted: z.number().int().nonnegative(),
        processed: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
    })
    .strict();

export interface GetProfileRequestOptions {
    readonly handle401AsSessionExpired?: boolean;
}

// Import all types and schemas needed for API functions
import type {
    ActiveRoleAssignmentResponse,
    CheckProposedPasswordRequest,
    CheckProposedPasswordResponse,
    CancelNativeAppLoginLinkRequest,
    CompleteCustomerPaymentLinkAttachmentRequest,
    LoginRequest,
    NativeAppLoginLinkResponse,
    PasswordlessLoginCodeVerificationRequest,
    PasswordlessLoginLinkExchangeRequest,
    PasswordlessLoginRequest,
    PasswordlessLoginChallengeResponse,
    SessionUserProfileDetailedResponse,
    SwapTokenForCookieRequest,
    SwapTokenForCookieResponse,
    SwapCookieForTokenResponse,
    VerifyTotpRequest,
    VerifyEmailRequest,
    VerifyEmailResponse,
    CreateCustomerSelfOnboardingRequest,
    CreateCustomerSelfOnboardingResponse,
    RequestPasswordResetRequest,
    RequestPasswordResetResponse,
    ValidatePasswordResetTokenRequest,
    ValidatePasswordResetTokenResponse,
    ResetPasswordRequest,
    ResetPasswordResponse,
    ChangePasswordRequest,
    SetupTotpResponse,
    EnableTotpRequest,
    GenerateBackupCodesResponse,
    SetDefaultRoleAssignmentRequest,
    SetDefaultRoleAssignmentUuidRequest,
    SetActiveRoleAssignmentResponse,
    SetActiveRoleAssignmentUuidRequest,
    StartImpersonationRequest,
    ImpersonationContextResponse,
    SendVerificationEmailResponse,
    SingleUseTokensSearchRequest,
    ListSingleUseTokensResponse,
    MfaActivationRequestResponse,
    MfaLoginChallengeResponse,
    MfaSetupStatusResponse,
    MfaPolicyStatusResponse,
    VerifyMfaActivationRequest,
    VerifyMfaLoginRequest,
    VerifyMfaLoginResponse,
} from './auth.types';
import type {
    ProvisionOrganizationalUserRequest,
    ProvisionOrganizationalUserResponse,
    ProvisionInternalUserResponse,
    ProvisionUserRequest,
    UpdateUserRequest,
    UpdateUserResponse,
    UserProfileDetailedPublicResponse,
    DeleteMyAccountRequest,
    DeleteMyAccountResponse,
    AssignRoleRequest,
    RevokeRoleRequest,
    UpdateRoleAssignmentRequest,
    ResetPasswordAdminRequest,
    RequestPasswordResetAdminRequest,
    SetUserMfaRequiredRequest,
    RequestAddEmailVerificationRequest,
    RequestAddEmailVerificationResponse,
    RequestAddPhoneVerificationRequest,
    RequestAddPhoneVerificationResponse,
    SetUserEmailsRequest,
    SetUserPhonesRequest,
    VerifyAddPhoneRequest,
} from './user.types';
import type {
    ApplyOrganizationUsersCsvImportRequest,
    ApplyOrganizationUsersCsvImportResponse,
    CreateOrganizationDocumentTemplateRequest,
    CreateIsoRequest,
    CreateIsoResponse,
    CreateMerchantRequest,
    CreateMerchantResponse,
    AssignMerchantToIsoRequest,
    SetOrganizationParentRequest,
    UpdateOrganizationRequest,
    GetOrganizationResponse,
    AssignMerchantToIsoResponse,
    SetOrganizationParentResponse,
    UpdateOrganizationResponse,
    ExportMerchantBackupRequest,
    ExportMerchantBackupResponse,
    GetMerchantChecklistResponse,
    GetMerchantChecklistCheckResponse,
    ListOrganizationDocumentTemplatesRequest,
    ListOrganizationDocumentTemplatesResponse,
    MerchantChecklistCheckCode,
    OrganizationDocumentTemplateOverrideDescriptor,
    RestoreMerchantBackupDryRunRequest,
    RestoreMerchantBackupDryRunResponse,
    RestoreMerchantBackupApplyRequest,
    RestoreMerchantBackupApplyResponse,
    PreviewOrganizationUsersCsvImportRequest,
    PreviewOrganizationUsersCsvImportResponse,
    SetOrganizationDocumentTemplateActiveRequest,
    UpdateOrganizationDocumentTemplateRequest,
    ValidateOrganizationDocumentTemplateRequest,
    ValidateOrganizationDocumentTemplateResponse,
} from './organization.types';
import type { RpaModuleName } from './backend_config.types';
import type {
    OrganizationOmnisearchSearchRequest,
    OrganizationOmnisearchPageResponse,
    UserOmnisearchPageRequest,
    UserOmnisearchPageResponse,
    ListSubsidiaryIsosPageResponse,
    ListSubsidiaryMerchantsPageResponse,
    ListDirectOrganizationUsersPageResponse,
} from './omnisearch.types';
import type {
    PublicUserListingSearchRequest,
    PublicUserListingPageResponse,
} from './user-listing.types';
import type {
    AcceptLegalDocumentRequest,
    CreateLegalDocumentRequest,
    CreateLegalDocumentResponse,
    GetLegalDocumentResponse,
    LegalDocumentsSearchRequest,
    ListPendingRequiredLegalDocumentsResponse,
    SearchLegalDocumentsPageResponse,
    UpdateLegalDocumentStatusRequest,
} from './legal-documents.types';
import type {
    ForensicOptionSearchRequest,
    ListForensicReferenceOptionsPageResponse,
    ListForensicSessionOptionsPageResponse,
    ListRecentRoleActivityPageResponse,
    ListRecentUserActivityPageResponse,
    ListTransactionLogPageResponse,
    RecentRoleActivitySearchRequest,
    RecentUserActivitySearchRequest,
    TransactionLogSearchRequest,
} from './transaction-log.types';
import type {
    CloudLoggingQueryRequest,
    CloudLoggingOptionSearchRequest,
    ListCloudLoggingEntriesPageResponse,
    ListCloudLoggingOptionsPageResponse,
    LogIngestRequest,
} from './logging.types';
import type {
    CustomerUsersSearchRequest,
    CustomerUsersPageResponse,
    MerchantCustomerRecordDetailsResponse,
    MerchantCustomerRecordIpwLookupRequest,
    MerchantCustomerRecordIpwLookupResponse,
    UpdateMerchantCustomerRecordRequest,
    MerchantCustomerRecordSearchRequest,
    MerchantCustomerRecordSearchPageResponse,
    UpsertIpwMerchantCustomerRecordRequest,
    UpsertIpwMerchantCustomerRecordResponse,
} from './customer.types';
import type {
    ExpireSessionUuidRequest,
    ExpireUserSessionRequest,
    GetSessionsResponse,
    SessionsSearchRequest,
    ListSessionsPageResponse,
    UpdateSessionConsoleRequest,
} from './session.types';
import type {
    CloverRuntimeEnvironmentAvailabilityResponse,
    EffectiveConfigResponse,
    CombinedConfigResponse,
    NavigateMenuWebsitesResponse,
    PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest,
    UpdateRoleAssignmentConfigRequest,
} from './samba-config.types';
import type { MerchantConfig, RoleAssignmentCustomConfig } from './backend_config.types';
import type {
    AwsCredentialsResponse,
    GetCallerIdentityResponse,
    GetSystemInfoResponse,
    GetSesDiagnosticsResponse,
} from './diagnostics.types';
import type {
    ListEncryptionKeyCredentialRotationAttemptsRequest,
    ListEncryptionKeyCredentialRotationAttemptsResponse,
    ListEncryptionKeyCredentialsRequest,
    ListEncryptionKeyCredentialsResponse,
    ListEncryptionKeysRequest,
    ListEncryptionKeysResponse,
    RekeyEncryptionKeyUsageRequest,
    RekeyEncryptionKeyUsageResponse,
    RotateEncryptionKeyResponse,
} from './encryption-key.types';
import type {
    CreateIpxCredentialRequest,
    ListSecretsResponse,
    GetSecretResponse,
    SecretMetadataResponse,
    SecretWithCredentialsResponse,
    CreateSecretRequest,
    CreateSecretResponse,
    ListSecretCredentialDefinitionsQuery,
    ListSecretCredentialDefinitionsResponse,
    UpdateSecretRequest,
    CredentialUsagesResponse,
    DisconnectCredentialUsagesRequest,
    DisconnectCredentialUsagesResponse,
    CreateDashboardWebsiteCredentialRequest,
    CredentialRotationBeginRequest,
    CredentialRotationBeginResponse,
    CredentialRotationFailRequest,
    CredentialRotationHistoryRequest,
    CredentialRotationHistoryResponse,
    CredentialRotationInjectedRequest,
    CredentialRotationManualPromotionRequest,
    CredentialRotationManualPromotionResponse,
    CredentialRotationPromoteRequest,
    CredentialRotationPromoteResponse,
    CredentialRotationSubmittedRequest,
    CreateOrganizationIpxCredentialPolicyRequest,
    IpxCredentialDefinitionSearchRequest,
    IpxCredentialDefinitionSearchResponse,
    SearchOrganizationIpxCredentialPolicyDefinitionsRequest,
    SearchOrganizationIpxCredentialPolicyDefinitionsResponse,
    IpxCredentialSummaryListItem,
    IpxCredentialRuntimeBootstrapRequest,
    IpxCredentialRuntimeBootstrapResponse,
    IpxCredentialRuntimeResolveRequest,
    IpxCredentialRuntimeResolveResponse,
    ListDashboardWebsiteCredentialsRequest,
    ListDashboardWebsiteCredentialsResponse,
    ListIpxCredentialsForPolicyResponse,
    ListOrganizationIpxCredentialPoliciesRequest,
    ListOrganizationIpxCredentialPoliciesResponse,
    ManualCredentialCaptureRequest,
    ManualCredentialCaptureResponse,
    OrganizationIpxCredentialPolicyLifecycleRequest,
    OrganizationIpxCredentialPolicyResponse,
    RevealIpxCredentialSecretFieldRequest,
    RevealIpxCredentialSecretFieldResponse,
    RpaBuilderAttachCredentialPolicyRequest,
    RpaBuilderAttachCredentialPolicyResponse,
    RpaBuilderBootstrapRequest,
    RpaBuilderBootstrapResponse,
    RpaBuilderCreateCredentialDefinitionRequest,
    RpaBuilderCreateWebResourceModelRequest,
    RpaBuilderDeleteCredentialDefinitionRequest,
    RpaBuilderDeleteCredentialDefinitionResponse,
    RpaBuilderResetInvalidLoginWorkflowRequest,
    RpaBuilderResetInvalidLoginWorkflowResponse,
    RpaBuilderDeleteWebResourceModelRequest,
    RpaBuilderDeleteWebResourceModelResponse,
    RpaBuilderDeactivateCredentialPolicyRequest,
    RpaBuilderDeactivateCredentialPolicyResponse,
    RpaBuilderFetchWebResourceModelRequest,
    RpaBuilderFetchWebResourceModelResponse,
    RpaBuilderListCredentialDefinitionsRequest,
    RpaBuilderListCredentialDefinitionsResponse,
    RpaBuilderListWebResourceModelsRequest,
    RpaBuilderListWebResourceModelsResponse,
    RpaBuilderReactivateCredentialPolicyRequest,
    RpaBuilderReactivateCredentialPolicyResponse,
    RpaBuilderSaveCredentialDefinitionResponse,
    RpaBuilderSaveWebsiteCredentialConfigurationRequest,
    RpaBuilderSaveWebsiteCredentialConfigurationResponse,
    RpaBuilderSaveWebResourceModelResponse,
    RpaBuilderUpdateCredentialDefinitionRequest,
    RpaBuilderReplaceWebResourceModelRequest,
    UpdateIpxCredentialRequest,
    SecretIdentityReference,
    SecretOrganizationUuidReference,
    SecretOrganizationReference,
    SecretUuidReference,
} from './secret.types';
import type {
    IpxRbsWorkflowRuntimeBootstrapRequest,
    IpxRbsWorkflowRuntimeBootstrapResponse,
} from './rbs-ips-workflow.types';
import {
    RpaBuilderSaveWebsiteCredentialConfigurationRequestSchema,
    RpaBuilderSaveWebsiteCredentialConfigurationResponseSchema,
} from './schemas/rbs-website.schemas';
import {
    CreateDashboardWebsiteCredentialRequestSchema,
    CredentialRotationBeginRequestSchema,
    CredentialRotationBeginResponseSchema,
    CredentialRotationFailRequestSchema,
    CredentialRotationHistoryRequestSchema,
    CredentialRotationHistoryResponseSchema,
    CredentialRotationInjectedRequestSchema,
    CredentialRotationManualPromotionRequestSchema,
    CredentialRotationManualPromotionResponseSchema,
    CredentialRotationPromoteRequestSchema,
    CredentialRotationPromoteResponseSchema,
    CredentialRotationSubmittedRequestSchema,
    CreateIpxCredentialRequestSchema,
    CreateOrganizationIpxCredentialPolicyRequestSchema,
    IpxCredentialDefinitionSearchRequestSchema,
    IpxCredentialDefinitionSearchResponseSchema,
    SearchOrganizationIpxCredentialPolicyDefinitionsRequestSchema,
    SearchOrganizationIpxCredentialPolicyDefinitionsResponseSchema,
    IpxCredentialSummaryListItemSchema,
    IpxCredentialRuntimeBootstrapRequestSchema,
    IpxCredentialRuntimeBootstrapResponseSchema,
    IpxCredentialRuntimeResolveRequestSchema,
    IpxCredentialRuntimeResolveResponseSchema,
    ListDashboardWebsiteCredentialsRequestSchema,
    ListDashboardWebsiteCredentialsResponseSchema,
    ListIpxCredentialsForPolicyResponseSchema,
    ListOrganizationIpxCredentialPoliciesRequestSchema,
    ListOrganizationIpxCredentialPoliciesResponseSchema,
    ManualCredentialCaptureRequestSchema,
    ManualCredentialCaptureResponseSchema,
    OrganizationIpxCredentialPolicyLifecycleRequestSchema,
    OrganizationIpxCredentialPolicyResponseSchema,
    RevealIpxCredentialSecretFieldRequestSchema,
    RevealIpxCredentialSecretFieldResponseSchema,
    RpaBuilderAttachCredentialPolicyRequestSchema,
    RpaBuilderAttachCredentialPolicyResponseSchema,
    RpaBuilderBootstrapRequestSchema,
    RpaBuilderBootstrapResponseSchema,
    RpaBuilderCreateCredentialDefinitionRequestSchema,
    RpaBuilderCreateWebResourceModelRequestSchema,
    RpaBuilderDeleteCredentialDefinitionRequestSchema,
    RpaBuilderDeleteCredentialDefinitionResponseSchema,
    RpaBuilderResetInvalidLoginWorkflowRequestSchema,
    RpaBuilderResetInvalidLoginWorkflowResponseSchema,
    RpaBuilderDeleteWebResourceModelRequestSchema,
    RpaBuilderDeleteWebResourceModelResponseSchema,
    RpaBuilderDeactivateCredentialPolicyRequestSchema,
    RpaBuilderDeactivateCredentialPolicyResponseSchema,
    RpaBuilderFetchWebResourceModelRequestSchema,
    RpaBuilderFetchWebResourceModelResponseSchema,
    RpaBuilderListCredentialDefinitionsRequestSchema,
    RpaBuilderListCredentialDefinitionsResponseSchema,
    RpaBuilderListWebResourceModelsRequestSchema,
    RpaBuilderListWebResourceModelsResponseSchema,
    RpaBuilderReactivateCredentialPolicyRequestSchema,
    RpaBuilderReactivateCredentialPolicyResponseSchema,
    RpaBuilderSaveCredentialDefinitionResponseSchema,
    RpaBuilderSaveWebResourceModelResponseSchema,
    RpaBuilderUpdateCredentialDefinitionRequestSchema,
    RpaBuilderReplaceWebResourceModelRequestSchema,
    ListSecretCredentialDefinitionsQuerySchema,
    ListSecretCredentialDefinitionsResponseSchema,
    SecretOrganizationReferenceSchema,
    SecretOrganizationUuidReferenceSchema,
    SecretUuidReferenceSchema,
    UpdateIpxCredentialRequestSchema,
} from './schemas/secret.schemas';
import {
    IpxRbsWorkflowRuntimeBootstrapRequestSchema,
    IpxRbsWorkflowRuntimeBootstrapResponseSchema,
} from './schemas/rbs-ips-workflow.schemas';
import type {
    ListPublicBundlesResponse,
    OrganizationPermissionAuditResponse,
    OrganizationScopedPermissionSourceOptionsResponse,
    PermissionsBundlePublic,
    PolicyPermissionBundleRoleAssignmentsSearchPageResponse,
    PolicyPermissionBundleRoleAssignmentsSearchRequest,
    PermissionSourceRoleAssignmentsSearchPageResponse,
    PermissionSourceRoleAssignmentsSearchRequest,
    RoleAssignmentPermissionsView,
    RoleAssignmentPermissionAuditResponse,
    GrantPermissionRequest,
    RevokePermissionRequest,
    RepairAuditedPermissionRequest,
    AssignBundleRequest,
    CreateSharedBundleRequest,
    UpdateBundleRequest,
    RoleEligibility,
    PolicyPermissionBundlesSearchRequest,
    PolicyPermissionBundlesSearchPageResponse,
    CreatePolicyPermissionBundleRequest,
    UpdatePolicyPermissionBundleRequest,
    ListPolicyPermissionGrantsResponse,
    GrantPolicyPermissionBundleRequest,
    ReorderPolicyPermissionGrantsRequest,
} from './permission.types';
import type {
    AllowedWebsiteTemplateBlastRadiusResponse,
    AllowedWebsiteTemplateMutationResponse,
    AllowedWebsiteTemplateSearchPageResponse,
    AllowedWebsiteTemplateSearchRequest,
    AllowedWebsiteTemplateUuidRequest,
    CreateAllowedWebsiteTemplateRequest,
    CreateLocalOrganizationAllowedWebsiteRequest,
    DeleteOrganizationAllowedWebsiteResponse,
    ListOrganizationAllowedWebsitesResponse,
    OrganizationAllowedWebsiteMutationResponse,
    PromoteOrganizationWebsiteToTemplateRequest,
    PromoteOrganizationWebsiteToTemplateResponse,
    ReferenceAllowedWebsiteTemplateToOrganizationRequest,
    SafeDeleteAllowedWebsiteTemplateResponse,
    UpdateAllowedWebsiteTemplateRequest,
    UpdateOrganizationAllowedWebsiteRequest,
} from './allowed-website-template.types';
import {
    OrganizationPermissionAuditResponseSchema,
    RoleAssignmentPermissionAuditResponseSchema,
} from './permission.types';
import type {
    CspViolationReportPayload,
    HealthResponse,
    MessageResponse,
    SambaModuleListResponse,
    SambaVersionResponse,
    SambaInstallerQaQuery,
    SambaLatestReleaseQuery,
    SambaLatestReleaseResponse,
} from './common.types';
import type { WindowsArtifactQa } from './windows-artifact-qa.types';
import type {
    GetVaultTransactionsResponse,
    GetTransactionDetailsResponse,
    GatewayTransactionDetailsFetchResult,
    GetReceiptDataResponse,
    GetPaymentAuditLogResponse,
    GetSavedCardPaymentMethodsRequest,
    GetSavedCardPaymentMethodsResponse,
    GetDelegateTokenRequest,
    GetDelegateTokenResponse,
    BinLookupRequest,
    BinLookupResponse,
    GetPaymentConfigurationRequest,
    PaymentConfigurationResponse,
    PaymentActionReadinessResponse,
    ListCardPaymentTerminalDevicesRequest,
    ListAccessibleTerminalsInfoRequest,
    ListAccessibleCredentialsInfoRequest,
    ListCardPaymentTerminalDevicesResponse,
    ListAccessibleCredentialsInfoResponse,
    ListAccessibleTerminalsInfoResponse,
    GetReconcilePaymentsCandidatesRequest,
    GetReconcilePaymentsCandidatesResponse,
    RefundTransactionRequest,
    RefundPaymentRequestRequest,
    RefundPaymentRequestResponse,
    RefundTransactionResponse,
    ResetPaymentRequestReconciliationResponse,
    ResetTransactionReconciliationResponse,
    VoidTransactionRequest,
    VoidPaymentRequestResponse,
    VoidTransactionResponse,
    TerminalSaleRequest,
    TerminalSaleResponse,
    TerminalTokenizationRequest,
    TerminalTokenizationResponse,
    TerminalAbortRequest,
    TerminalAbortResponse,
    RecordTokenRequest,
    RecordTokenResponse,
    SubmitCardTokenPaymentRequest,
    ProcessStoredPaymentMethodPaymentRequest,
    ProcessCardTokenPaymentResponse,
    ProcessCardNotPresentPaymentResponse,
    ProcessAchPaymentRequest,
    ProcessAchPaymentResponse,
    RecordReconciliationRequest,
    RecordReconciliationResponse,
    RecordPaymentRequestReconciliationRequest,
    RecordPaymentRequestReconciliationResponse,
    CreateDraftPaymentRequestRequest,
    CreateDraftPaymentRequestResponse,
    PaymentRequestOwnerCandidatesResponse,
    UploadPaymentRequestDocumentResponse,
    FinalizeSendPaymentRequestRequest,
    FinalizeSendPaymentRequestResponse,
    MerchantPaymentRequestSearchRequest,
    MerchantPaymentRequestListResponse,
    CustomerPaymentMethodListRequest,
    MerchantPaymentMethodTokenListRequest,
    MerchantPaymentMethodTokenDetailsView,
    MerchantPaymentMethodTokenListResponse,
    PaymentRequestDeliveryEventList,
    PaymentRequestDetailsView,
    CustomerPaymentMethodListResponse,
    CustomerPaymentRequestDetailsRequest,
    MerchantPaymentRequestDetailsRequest,
    CustomerPaymentMethodBootstrapRequest,
    CustomerPaymentMethodBootstrapResponse,
    CustomerPaymentMethodBinLookupRequest,
    CreateCustomerPaymentMethodRequest,
    CreateCustomerPaymentMethodResponse,
    CustomerPortalDashboardRequest,
    CustomerPortalDashboardResponse,
    CustomerPaymentHistoryRequest,
    CustomerPaymentHistoryResponse,
    CustomerPaymentRequestSearchRequest,
    CustomerPaymentRequestListResponse,
    GetPaymentAuditLogSearchRequest,
    GetVaultTransactionsSearchRequest,
    PaymentTransactionLifecycleEventSearchRequest,
    PaymentTransactionLifecycleEventListResponse,
    ResendPaymentRequestRequest,
    UpdateCustomerPaymentMethodRequest,
    UpdateCustomerPaymentMethodResponse,
    CancelAndReissuePaymentRequestRequest,
    CancelAndReissuePaymentRequestResponse,
    PublicPaymentLinkActionResponse,
    PublicPaymentRequestView,
    GuestPaymentEntryContextView,
    AuthenticatedPaymentEntryContextView,
    GuestPaymentRequestDocumentAccessResponse,
    GuestPaymentSuccessView,
    GuestBinLookupRequest,
    GuestProcessCardTokenPaymentRequest,
    AuthenticatedProcessCardTokenPaymentRequest,
    GuestProcessStoredPaymentMethodPaymentRequest,
    SendgridEventWebhookPayload,
    TwilioStatusCallbackPayload,
} from './payment.types';
import type {
    ProcessorSearchRequest,
    ProcessorSearchPageResponse,
    CreateProcessorRequest,
    GetProcessorResponse,
    UpdateProcessorRequest,
    MerchantAccountSearchRequest,
    MerchantAccountSearchPageResponse,
    CreateMerchantAccountRequest,
    UpdateMerchantAccountRequest,
    GetMerchantAccountResponse,
    LogicalTerminalSearchRequest,
    LogicalTerminalSearchPageResponse,
    CreateLogicalTerminalRequest,
    UpdateLogicalTerminalRequest,
    GetLogicalTerminalResponse,
    MerchantOrganizationSearchRequest,
    MerchantOrganizationSearchPageResponse,
    PreviewLogicalTerminalOrganizationChangeRequest,
    PreviewLogicalTerminalOrganizationChangeResponse,
    TerminalDeviceSearchRequest,
    TerminalDeviceSearchPageResponse,
    CreateTerminalDeviceRequest,
    UpdateTerminalDeviceRequest,
    GetTerminalDeviceResponse,
} from './payments_infrastructure.types';
import {
    CompleteCustomerPaymentLinkAttachmentRequestSchema,
    CheckProposedPasswordRequestSchema,
    CheckProposedPasswordResponseSchema,
    LoginResponseSchema,
    NativeAppLoginLinkResponseSchema,
    CancelNativeAppLoginLinkRequestSchema,
    PasswordlessLoginChallengeResponseSchema,
    PasswordlessLoginCodeVerificationRequestSchema,
    PasswordlessLoginLinkExchangeRequestSchema,
    PasswordlessLoginRequestSchema,
    SessionUserProfileDetailedResponseSchema,
    NullableActiveRoleAssignmentResponseSchema,
    VerifyTotpResponseSchema,
    SwapCookieForTokenResponseSchema,
    SwapTokenForCookieRequestSchema,
    SwapTokenForCookieResponseSchema,
    VerifyEmailResponseSchema,
    CreateCustomerSelfOnboardingResponseSchema,
    RequestPasswordResetResponseSchema,
    ValidatePasswordResetTokenResponseSchema,
    ResetPasswordResponseSchema,
    SendVerificationEmailResponseSchema,
    SetupTotpResponseSchema,
    GenerateBackupCodesResponseSchema,
    SetActiveRoleAssignmentResponseSchema,
    StartImpersonationRequestSchema,
    ImpersonationContextResponseSchema,
    SingleUseTokensSearchRequestSchema,
    ListSingleUseTokensResponseSchema,
    MfaActivationRequestResponseSchema,
    MfaLoginChallengeResponseSchema,
    MfaSetupStatusResponseSchema,
    MfaPolicyStatusResponseSchema,
    VerifyMfaActivationRequestSchema,
    VerifyMfaLoginRequestSchema,
    VerifyMfaLoginResponseSchema,
} from './schemas/auth.schemas';
import {
    ProvisionOrganizationalUserResponseSchema,
    ProvisionInternalUserResponseSchema,
    UserProfileDetailedPublicResponseSchema,
    DeleteMyAccountResponseSchema,
    ResetPasswordAdminRequestSchema,
    RequestPasswordResetAdminRequestSchema,
    SetUserMfaRequiredRequestSchema,
    UpdateUserResponseSchema,
    RequestAddEmailVerificationRequestSchema,
    RequestAddEmailVerificationResponseSchema,
    RequestAddPhoneVerificationRequestSchema,
    RequestAddPhoneVerificationResponseSchema,
    SetUserPhonesRequestSchema,
    VerifyAddPhoneRequestSchema,
} from './schemas/user.schemas';
import {
    ExpireSessionUuidRequestSchema,
    ExpireUserSessionRequestSchema,
    GetSessionsResponseSchema,
    SessionsSearchRequestSchema,
    ListSessionsPageResponseSchema,
    UpdateSessionConsoleRequestSchema,
} from './schemas/session.schemas';
import {
    ApplyOrganizationUsersCsvImportRequestSchema,
    ApplyOrganizationUsersCsvImportResponseSchema,
    CreateOrganizationDocumentTemplateRequestSchema,
    GetOrganizationResponseSchema,
    AssignMerchantToIsoResponseSchema,
    OrganizationDocumentTemplateOverrideDescriptorSchema,
    OrganizationUuidParamSchema,
    SetOrganizationParentResponseSchema,
    UpdateOrganizationResponseSchema,
    CreateIsoResponseSchema,
    CreateMerchantResponseSchema,
    ExportMerchantBackupRequestSchema,
    ExportMerchantBackupResponseSchema,
    GetMerchantChecklistResponseSchema,
    GetMerchantChecklistCheckResponseSchema,
    ListOrganizationDocumentTemplatesRequestSchema,
    ListOrganizationDocumentTemplatesResponseSchema,
    RestoreMerchantBackupDryRunRequestSchema,
    RestoreMerchantBackupDryRunResponseSchema,
    RestoreMerchantBackupApplyRequestSchema,
    RestoreMerchantBackupApplyResponseSchema,
    PreviewOrganizationUsersCsvImportRequestSchema,
    PreviewOrganizationUsersCsvImportResponseSchema,
    SetOrganizationDocumentTemplateActiveRequestSchema,
    UpdateOrganizationDocumentTemplateRequestSchema,
    ValidateOrganizationDocumentTemplateRequestSchema,
    ValidateOrganizationDocumentTemplateResponseSchema,
} from './schemas/organization.schemas';
import {
    OrganizationOmnisearchSearchRequestSchema,
    OrganizationOmnisearchPageResponseSchema,
    UserOmnisearchPageResponseSchema,
    ListSubsidiaryIsosPageResponseSchema,
    ListSubsidiaryMerchantsPageResponseSchema,
    ListDirectOrganizationUsersPageResponseSchema,
} from './schemas/omnisearch.schemas';
import {
    PublicUserListingPageResponseSchema,
    PublicUserListingSearchRequestSchema,
} from './schemas/user-listing.schemas';
import {
    AcceptLegalDocumentRequestSchema,
    CreateLegalDocumentRequestSchema,
    CreateLegalDocumentResponseSchema,
    GetLegalDocumentResponseSchema,
    LegalDocumentUuidParamSchema,
    LegalDocumentsSearchRequestSchema,
    ListPendingRequiredLegalDocumentsResponseSchema,
    SearchLegalDocumentsPageResponseSchema,
} from './schemas/legal-documents.schemas';
import {
    ForensicOptionSearchRequestSchema,
    ListForensicReferenceOptionsPageResponseSchema,
    ListForensicSessionOptionsPageResponseSchema,
    ListRecentRoleActivityPageResponseSchema,
    ListRecentUserActivityPageResponseSchema,
    ListTransactionLogPageResponseSchema,
    RecentRoleActivitySearchRequestSchema,
    RecentUserActivitySearchRequestSchema,
    TransactionLogSearchRequestSchema,
} from './schemas/transaction-log.schemas';
import {
    CustomerUsersSearchRequestSchema,
    CustomerUsersPageResponseSchema,
    MerchantCustomerRecordDetailsResponseSchema,
    MerchantCustomerRecordIpwLookupRequestSchema,
    MerchantCustomerRecordIpwLookupResponseSchema,
    MerchantCustomerRecordSearchRequestSchema,
    MerchantCustomerRecordSearchPageResponseSchema,
    UpsertIpwMerchantCustomerRecordRequestSchema,
    UpsertIpwMerchantCustomerRecordResponseSchema,
    UpdateMerchantCustomerRecordRequestSchema,
} from './schemas/customer.schemas';
import {
    CloverRuntimeEnvironmentAvailabilityResponseSchema,
    ConfigResponseSchema,
    CombinedConfigResponseSchema,
    EffectiveConfigResponseSchema,
    NavigateMenuWebsitesResponseSchema,
    PatchActiveRoleAssignmentUserRoleAssignmentConfigRequestSchema,
    RoleAssignmentUserConfigResponseSchema,
    UpdateRoleAssignmentConfigRequestSchema,
} from './schemas/samba-config.schemas';
import {
    GetVaultTransactionsResponseSchema,
    GetVaultTransactionsSearchRequestSchema,
    PaymentTransactionLifecycleEventListResponseSchema,
    PaymentTransactionLifecycleEventSearchRequestSchema,
    GetTransactionDetailsResponseSchema,
    GatewayTransactionDetailsFetchResultSchema,
    GetReceiptDataResponseSchema,
    PaymentTransactionUuidParamSchema,
    GetPaymentAuditLogResponseSchema,
    GetPaymentAuditLogSearchRequestSchema,
    GetSavedCardPaymentMethodsRequestSchema,
    GetSavedCardPaymentMethodsResponseSchema,
    GetDelegateTokenRequestSchema,
    GetDelegateTokenResponseSchema,
    BinLookupRequestSchema,
    BinLookupResponseSchema,
    GetPaymentConfigurationRequestSchema,
    PaymentConfigurationResponseSchema,
    PaymentActionReadinessResponseSchema,
    ListCardPaymentTerminalDevicesRequestSchema,
    ListAccessibleTerminalsInfoRequestSchema,
    ListAccessibleCredentialsInfoRequestSchema,
    ListCardPaymentTerminalDevicesResponseSchema,
    ListAccessibleCredentialsInfoResponseSchema,
    ListAccessibleTerminalsInfoResponseSchema,
    GetReconcilePaymentsCandidatesResponseSchema,
    RefundPaymentRequestResponseSchema,
    RefundTransactionResponseSchema,
    ResetPaymentRequestReconciliationResponseSchema,
    ResetTransactionReconciliationResponseSchema,
    VoidPaymentRequestResponseSchema,
    VoidTransactionResponseSchema,
    TerminalSaleResponseSchema,
    TerminalTokenizationRequestSchema,
    TerminalTokenizationResponseSchema,
    TerminalAbortResponseSchema,
    RecordTokenResponseSchema,
    ProcessCardTokenPaymentResponseSchema,
    ProcessCardNotPresentPaymentResponseSchema,
    ProcessAchPaymentResponseSchema,
    RecordReconciliationResponseSchema,
    RecordPaymentRequestReconciliationRequestSchema,
    RecordPaymentRequestReconciliationResponseSchema,
    CreateDraftPaymentRequestRequestSchema,
    CreateDraftPaymentRequestResponseSchema,
    PaymentRequestOwnerCandidatesResponseSchema,
    UploadPaymentRequestDocumentResponseSchema,
    FinalizeSendPaymentRequestRequestSchema,
    FinalizeSendPaymentRequestResponseSchema,
    MerchantPaymentRequestSearchRequestSchema,
    MerchantPaymentRequestListResponseSchema,
    CustomerPaymentMethodListRequestSchema,
    MerchantPaymentMethodTokenListRequestSchema,
    MerchantPaymentMethodTokenDetailsViewSchema,
    MerchantPaymentMethodTokenListResponseSchema,
    PaymentRequestDeliveryEventListSchema,
    PaymentRequestDetailsViewSchema,
    CustomerPaymentMethodListResponseSchema,
    CustomerPaymentRequestDetailsRequestSchema,
    MerchantPaymentRequestDetailsRequestSchema,
    CustomerPaymentMethodBootstrapRequestSchema,
    CustomerPaymentMethodBootstrapResponseSchema,
    CustomerPaymentMethodBinLookupRequestSchema,
    CreateCustomerPaymentMethodRequestSchema,
    CreateCustomerPaymentMethodResponseSchema,
    CustomerPortalDashboardRequestSchema,
    CustomerPortalDashboardResponseSchema,
    CustomerPaymentHistoryRequestSchema,
    CustomerPaymentHistoryResponseSchema,
    CustomerPaymentRequestSearchRequestSchema,
    CustomerPaymentRequestListResponseSchema,
    ResendPaymentRequestRequestSchema,
    UpdateCustomerPaymentMethodRequestSchema,
    UpdateCustomerPaymentMethodResponseSchema,
    CancelAndReissuePaymentRequestRequestSchema,
    CancelAndReissuePaymentRequestResponseSchema,
    PublicPaymentLinkActionResponseSchema,
    PublicPaymentRequestViewSchema,
    GuestPaymentEntryContextViewSchema,
    AuthenticatedPaymentEntryContextViewSchema,
    GuestPaymentRequestDocumentAccessResponseSchema,
    GuestPaymentSuccessViewSchema,
    GuestBinLookupRequestSchema,
    GuestProcessCardTokenPaymentRequestSchema,
    AuthenticatedProcessCardTokenPaymentRequestSchema,
    GuestProcessStoredPaymentMethodPaymentRequestSchema,
    ProcessStoredPaymentMethodPaymentRequestSchema,
    SendgridEventWebhookPayloadSchema,
    TwilioStatusCallbackPayloadSchema,
} from './schemas/payment.schemas';
import {
    ProcessorSearchRequestSchema,
    ProcessorSearchPageResponseSchema,
    CreateProcessorRequestSchema,
    GetProcessorResponseSchema,
    UpdateProcessorRequestSchema,
    MerchantAccountSearchRequestSchema,
    MerchantAccountSearchPageResponseSchema,
    MerchantAccountUuidParamSchema,
    CreateMerchantAccountRequestSchema,
    UpdateMerchantAccountRequestSchema,
    GetMerchantAccountResponseSchema,
    LogicalTerminalSearchRequestSchema,
    LogicalTerminalSearchPageResponseSchema,
    LogicalTerminalUuidParamSchema,
    CreateLogicalTerminalRequestSchema,
    UpdateLogicalTerminalRequestSchema,
    GetLogicalTerminalResponseSchema,
    MerchantOrganizationSearchRequestSchema,
    MerchantOrganizationSearchPageResponseSchema,
    PreviewLogicalTerminalOrganizationChangeRequestSchema,
    PreviewLogicalTerminalOrganizationChangeResponseSchema,
    TerminalDeviceSearchRequestSchema,
    TerminalDeviceSearchPageResponseSchema,
    TerminalDeviceUuidParamSchema,
    CreateTerminalDeviceRequestSchema,
    UpdateTerminalDeviceRequestSchema,
    GetTerminalDeviceResponseSchema,
} from './schemas/payments-infrastructure.schemas';
import {
    CspViolationReportSchema,
    HealthResponseSchema,
    SambaLatestReleaseResponseSchema,
    SambaInstallerQaQuerySchema,
    SambaModuleListResponseSchema,
    SambaVersionResponseSchema,
} from './schemas/common.schemas';
import { WindowsArtifactQaSchema } from './schemas/windows-artifact-qa.schemas';
import {
    EnvironmentVariablesResponseSchema,
    AwsCredentialsResponseSchema,
    GetCallerIdentityResponseSchema,
    GetSystemInfoResponseSchema,
    GetSesDiagnosticsResponseSchema,
} from './schemas/diagnostics.schemas';
import {
    ListEncryptionKeyCredentialRotationAttemptsRequestSchema,
    ListEncryptionKeyCredentialRotationAttemptsResponseSchema,
    ListEncryptionKeyCredentialsRequestSchema,
    ListEncryptionKeyCredentialsResponseSchema,
    ListEncryptionKeysRequestSchema,
    ListEncryptionKeysResponseSchema,
    RekeyEncryptionKeyUsageRequestSchema,
    RekeyEncryptionKeyUsageResponseSchema,
    RotateEncryptionKeyResponseSchema,
} from './schemas/encryption-key.schemas';
import {
    ListSecretsResponseSchema,
    GetSecretResponseSchema,
    SecretMetadataResponseSchema,
    SecretWithCredentialsResponseSchema,
    CreateSecretRequestSchema,
    CreateSecretResponseSchema,
    ListSecretsForOrganizationQuerySchema,
    CredentialUsagesResponseSchema,
    DisconnectCredentialUsagesRequestSchema,
    DisconnectCredentialUsagesResponseSchema,
} from './schemas/secret.schemas';
import type { ListSecretsForOrganizationQuery } from './secret.types';
import {
    AssignBundleRequestSchema,
    GrantPermissionRequestSchema,
    ListPublicBundlesResponseSchema,
    PermissionsBundlePublicSchema,
    RepairAuditedPermissionRequestSchema,
    SharedPermissionBundlesSearchPageResponseSchema,
    SharedPermissionBundlesSearchRequestSchema,
    RevokePermissionRequestSchema,
    RoleEligibilitySchema,
    RoleAssignmentPermissionsViewSchema,
    PermissionSourceRoleAssignmentsSearchRequestSchema,
    PermissionSourceRoleAssignmentsSearchPageResponseSchema,
    PolicyPermissionBundleRoleAssignmentsSearchRequestSchema,
    PolicyPermissionBundleRoleAssignmentsSearchPageResponseSchema,
    OrganizationScopedPermissionSourceOptionsResponseSchema,
    PolicyPermissionBundlesSearchRequestSchema,
    PolicyPermissionBundlesSearchPageResponseSchema,
    ListPolicyPermissionGrantsResponseSchema,
    GrantPolicyPermissionBundleRequestSchema,
    ReorderPolicyPermissionGrantsRequestSchema,
} from './schemas/permission.schemas';
import {
    AllowedWebsiteTemplateBlastRadiusResponseSchema,
    AllowedWebsiteTemplateMutationResponseSchema,
    AllowedWebsiteTemplateSearchPageResponseSchema,
    AllowedWebsiteTemplateSearchRequestSchema,
    AllowedWebsiteTemplateUuidRequestSchema,
    CreateAllowedWebsiteTemplateRequestSchema,
    CreateLocalOrganizationAllowedWebsiteRequestSchema,
    DeleteOrganizationAllowedWebsiteResponseSchema,
    ListOrganizationAllowedWebsitesResponseSchema,
    OrganizationAllowedWebsiteMutationResponseSchema,
    PromoteOrganizationWebsiteToTemplateRequestSchema,
    PromoteOrganizationWebsiteToTemplateResponseSchema,
    ReferenceAllowedWebsiteTemplateToOrganizationRequestSchema,
    UpdateAllowedWebsiteTemplateRequestSchema,
    UpdateOrganizationAllowedWebsiteRequestSchema,
    SafeDeleteAllowedWebsiteTemplateResponseSchema,
} from './schemas/allowed-website-template.schemas';
import type {
    SharedPermissionBundlesSearchPageResponse,
    SharedPermissionBundlesSearchRequest,
} from './permission.types';
import {
    CanCreateUserResponseSchema,
    CanCreateUserQuerySchema,
    CanDeleteUserQuerySchema,
    CanModifyUserQuerySchema,
    CanModifyUserResponseSchema,
    CanDeleteUserResponseSchema,
    HasAccessToOrganizationQuerySchema,
    HasAccessToOrganizationResponseSchema,
    AccessibleOrganizationIdsResponseSchema,
    AccessibleUserIdsResponseSchema,
    AccessibleOrganizationsResponseSchema,
    AccessibleUsersResponseSchema,
    IsAuthorizedRequestSchema,
    IsAuthorizedResponseSchema,
    type IsAuthorizedRequest,
    type AccessibleOrganizationIdsRequest,
    type AccessibleUserIdsRequestNoPkey,
    HasAccessToUserResponseSchema,
    CanImpersonateRoleAssignmentResponseSchema,
} from './schemas/authorization.schemas';
import {
    MessageResponseSchema,
    VoidResponseSchema,
} from './schemas/common.schemas';
import {
    CloudLoggingQueryRequestSchema,
    CloudLoggingOptionSearchRequestSchema,
    ListCloudLoggingEntriesPageResponseSchema,
    ListCloudLoggingOptionsPageResponseSchema,
    LogIngestRequestSchema,
} from './schemas/logging.schemas';

/**
 * Factory function to create PILLAR API client instances
 * @param onSessionExpired Optional callback for handling 401 responses
 * @param baseUrl Base URL for the API (defaults to '/api')
 * @param onTraceContext Optional callback for propagating trace context
 * @param onValidationError Optional callback for response validation failures
 * @returns Object containing all API client functions
 */
export function createPillarApi(
    onSessionExpired?: () => void,
    baseUrl: string = '/api',
    onTraceContext?: (traceHeaderValue: string) => void,
    onValidationError?: (error: ApiValidationError) => void
) {
    const options: PillarApiClientOptions = {
        onSessionExpired,
        baseUrl,
        enableIdempotencyHeaders: true,
        onTraceContext,
        onValidationError,
    };
    return createPillarApiWithOptions(options);
}

export const IpxCredentialManagementUuidSchema: z.ZodType<string> = z.string().uuid();

function buildIpxCredentialManagementOrganizationPoliciesEndpoint(
    data: ListOrganizationIpxCredentialPoliciesRequest
): string {
    const validatedData: ListOrganizationIpxCredentialPoliciesRequest =
        ListOrganizationIpxCredentialPoliciesRequestSchema.parse(data);
    const queryParams: URLSearchParams = new URLSearchParams();
    if (validatedData.status !== undefined) {
        queryParams.set('status', validatedData.status);
    }
    if (validatedData.limit !== undefined) {
        queryParams.set('limit', String(validatedData.limit));
    }
    if (validatedData.cursor !== undefined) {
        queryParams.set('cursor', validatedData.cursor);
    }
    const encodedOrganizationUuid: string = encodeURIComponent(validatedData.organization_uuid);
    const queryString: string = queryParams.toString();
    const endpoint = `/portal/v1/ipx-credential-management/organizations/${encodedOrganizationUuid}/policies`;
    return queryString.length === 0 ? endpoint : `${endpoint}?${queryString}`;
}

function buildIpxCredentialManagementDashboardWebsiteCredentialsEndpoint(
    data: ListDashboardWebsiteCredentialsRequest
): string {
    const validatedData: ListDashboardWebsiteCredentialsRequest =
        ListDashboardWebsiteCredentialsRequestSchema.parse(data);
    const encodedOrganizationUuid: string = encodeURIComponent(validatedData.organization_uuid);
    return `/portal/v1/ipx-credential-management/organizations/${encodedOrganizationUuid}/dashboard-website-credentials`;
}

function buildIpxCredentialManagementDashboardWebsiteCredentialEndpoint(
    data: Pick<
        CreateDashboardWebsiteCredentialRequest,
        'organization_uuid' | 'organization_ipx_credential_policy_uuid'
    >
): string {
    const validatedOrganizationUuid = z.string().uuid().parse(data.organization_uuid);
    const validatedPolicyUuid = z
        .string()
        .uuid()
        .parse(data.organization_ipx_credential_policy_uuid);
    return `/portal/v1/ipx-credential-management/organizations/${encodeURIComponent(
        validatedOrganizationUuid
    )}/dashboard-website-credentials/${encodeURIComponent(validatedPolicyUuid)}`;
}

function buildIpxCredentialManagementPolicyEndpoint(
    policyUuid: string,
    suffix: string = ''
): string {
    const validatedPolicyUuid: string = IpxCredentialManagementUuidSchema.parse(policyUuid);
    return `/portal/v1/ipx-credential-management/policies/${encodeURIComponent(
        validatedPolicyUuid
    )}${suffix}`;
}

function buildIpxCredentialManagementCredentialEndpoint(
    credentialUuid: string,
    suffix: string = ''
): string {
    const validatedCredentialUuid: string = IpxCredentialManagementUuidSchema.parse(credentialUuid);
    return `/portal/v1/ipx-credential-management/credentials/${encodeURIComponent(
        validatedCredentialUuid
    )}${suffix}`;
}

function buildIpxCredentialManagementCredentialRotationHistoryEndpoint(
    data: CredentialRotationHistoryRequest
): string {
    const validatedData: CredentialRotationHistoryRequest =
        CredentialRotationHistoryRequestSchema.parse(data);
    const queryParams = new URLSearchParams();
    if (validatedData.limit !== undefined) {
        queryParams.set('limit', String(validatedData.limit));
    }
    if (validatedData.cursor !== undefined) {
        queryParams.set('cursor', validatedData.cursor);
    }
    if (validatedData.state !== undefined) {
        queryParams.set('state', validatedData.state);
    }
    const queryString = queryParams.toString();
    const endpoint = buildIpxCredentialManagementCredentialEndpoint(
        validatedData.credential_uuid,
        '/rotation-attempts'
    );
    return queryString.length === 0 ? endpoint : `${endpoint}?${queryString}`;
}

function buildRpaBuilderDefinitionEndpoint(definitionUuid: string, suffix: string = ''): string {
    const validatedDefinitionUuid: string = z.string().uuid().parse(definitionUuid);
    return `/portal/v1/rpa-builder/credential-definitions/${encodeURIComponent(
        validatedDefinitionUuid
    )}${suffix}`;
}

function buildRpaBuilderListCredentialDefinitionsEndpoint(
    data: RpaBuilderListCredentialDefinitionsRequest
): string {
    const validatedData: RpaBuilderListCredentialDefinitionsRequest =
        RpaBuilderListCredentialDefinitionsRequestSchema.parse(data);
    const queryParams: URLSearchParams = new URLSearchParams();
    if (validatedData.system_id !== undefined) {
        queryParams.set('system_id', String(validatedData.system_id));
    }
    if (validatedData.credential_definition_key !== undefined) {
        queryParams.set('credential_definition_key', validatedData.credential_definition_key);
    }
    if (validatedData.status !== undefined) {
        queryParams.set('status', validatedData.status);
    }
    const queryString = queryParams.toString();
    return queryString.length === 0
        ? '/portal/v1/rpa-builder/credential-definitions'
        : `/portal/v1/rpa-builder/credential-definitions?${queryString}`;
}

function buildRpaBuilderWebResourceModelEndpoint(modelUuid: string): string {
    const validatedModelUuid: string = z.string().uuid().parse(modelUuid);
    return `/portal/v1/rpa-builder/web-resource-models/${encodeURIComponent(validatedModelUuid)}`;
}

function buildRpaBuilderListWebResourceModelsEndpoint(
    data: RpaBuilderListWebResourceModelsRequest
): string {
    const validatedData: RpaBuilderListWebResourceModelsRequest =
        RpaBuilderListWebResourceModelsRequestSchema.parse(data);
    const queryParams: URLSearchParams = new URLSearchParams();
    if (validatedData.system_id !== undefined) {
        queryParams.set('system_id', String(validatedData.system_id));
    }
    if (validatedData.initial_navigation_url !== undefined) {
        queryParams.set('initial_navigation_url', validatedData.initial_navigation_url);
    }
    if (validatedData.status !== undefined) {
        queryParams.set('status', validatedData.status);
    }
    const queryString = queryParams.toString();
    return queryString === ''
        ? '/portal/v1/rpa-builder/web-resource-models'
        : `/portal/v1/rpa-builder/web-resource-models?${queryString}`;
}

function buildAllowedWebsiteTemplateEndpoint(templateUuid: string, suffix: string = ''): string {
    const validatedTemplateUuid: string = z.string().uuid().parse(templateUuid);
    return `/portal/v1/allowed_website_templates/${encodeURIComponent(
        validatedTemplateUuid
    )}${suffix}`;
}

function buildOrganizationAllowedWebsitesEndpoint(
    organizationUuid: string,
    suffix: string = ''
): string {
    const validatedOrganizationUuid: string = z.string().uuid().parse(organizationUuid);
    return `/portal/v1/organization/${encodeURIComponent(
        validatedOrganizationUuid
    )}/allowed_websites${suffix}`;
}

function buildOrganizationAllowedWebsiteEndpoint(
    organizationUuid: string,
    organizationAllowedWebsiteUuid: string,
    suffix: string = ''
): string {
    const validatedOrganizationWebsiteUuid: string = z
        .string()
        .uuid()
        .parse(organizationAllowedWebsiteUuid);
    return buildOrganizationAllowedWebsitesEndpoint(
        organizationUuid,
        `/${encodeURIComponent(validatedOrganizationWebsiteUuid)}${suffix}`
    );
}

/**
 * Factory function to create PILLAR API client instances with custom fetch/headers.
 */
export function createPillarApiWithOptions(options: PillarApiClientOptions) {
    const {
        baseUrl,
        fetchImplementation,
        getRequestHeaders,
        onResponse,
        buildRequestUrl,
        requestWithSchema,
        requestTextWithSchema,
    } = createPillarApiRequestExecutor(options);

    const authApi = {
        login: (data: LoginRequest) =>
            requestWithSchema('/portal/v1/user_management/any_user/login', LoginResponseSchema, {
                method: 'POST',
                body: data,
                handle401AsSessionExpired: false,
            }),

        requestPasswordlessLogin: (data: PasswordlessLoginRequest) =>
            requestWithSchema<PasswordlessLoginChallengeResponse>(
                '/portal/v1/user_management/any_user/passwordless/request',
                PasswordlessLoginChallengeResponseSchema,
                {
                    method: 'POST',
                    body: PasswordlessLoginRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        verifyPasswordlessLoginCode: (data: PasswordlessLoginCodeVerificationRequest) =>
            requestWithSchema(
                '/portal/v1/user_management/any_user/passwordless/verify-code',
                LoginResponseSchema,
                {
                    method: 'POST',
                    body: PasswordlessLoginCodeVerificationRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        exchangePasswordlessLoginLink: (data: PasswordlessLoginLinkExchangeRequest) =>
            requestWithSchema(
                '/portal/v1/user_management/any_user/passwordless/exchange-link',
                LoginResponseSchema,
                {
                    method: 'POST',
                    body: PasswordlessLoginLinkExchangeRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        completeCurrentCustomerPaymentLinkAttachment: (
            data: CompleteCustomerPaymentLinkAttachmentRequest
        ) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/complete_customer_payment_link_attachment',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: CompleteCustomerPaymentLinkAttachmentRequestSchema.parse(data),
                }
            ),

        checkProposedPassword: (data: CheckProposedPasswordRequest) =>
            requestWithSchema<CheckProposedPasswordResponse>(
                '/portal/v1/user_management/any_user/check_proposed_password',
                CheckProposedPasswordResponseSchema,
                {
                    method: 'POST',
                    body: CheckProposedPasswordRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        verifyTotp: (data: VerifyTotpRequest) =>
            requestWithSchema(
                '/portal/v1/user_management/any_user/verify_totp',
                VerifyTotpResponseSchema,
                {
                    method: 'POST',
                    body: data,
                    handle401AsSessionExpired: false,
                }
            ),

        requestEmailMfaLogin: () =>
            requestWithSchema<MfaLoginChallengeResponse>(
                '/portal/v1/user_management/any_user/request_email_mfa_login',
                MfaLoginChallengeResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        verifyEmailMfaLogin: (data: VerifyMfaLoginRequest) =>
            requestWithSchema<VerifyMfaLoginResponse>(
                '/portal/v1/user_management/any_user/verify_email_mfa_login',
                VerifyMfaLoginResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaLoginRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        requestSmsMfaLogin: () =>
            requestWithSchema<MfaLoginChallengeResponse>(
                '/portal/v1/user_management/any_user/request_sms_mfa_login',
                MfaLoginChallengeResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        verifySmsMfaLogin: (data: VerifyMfaLoginRequest) =>
            requestWithSchema<VerifyMfaLoginResponse>(
                '/portal/v1/user_management/any_user/verify_sms_mfa_login',
                VerifyMfaLoginResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaLoginRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        getMfaSetupStatus: () =>
            requestWithSchema<MfaSetupStatusResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/status',
                MfaSetupStatusResponseSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        setupLoginMfaTotp: () =>
            requestWithSchema<SetupTotpResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/totp/setup',
                SetupTotpResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        verifyLoginMfaTotpSetup: (data: EnableTotpRequest) =>
            requestWithSchema<VerifyMfaLoginResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/totp/verify',
                VerifyMfaLoginResponseSchema,
                {
                    method: 'POST',
                    body: data,
                    handle401AsSessionExpired: false,
                }
            ),

        requestLoginMfaEmailSetup: () =>
            requestWithSchema<MfaActivationRequestResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/email/request',
                MfaActivationRequestResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        verifyLoginMfaEmailSetup: (data: VerifyMfaActivationRequest) =>
            requestWithSchema<VerifyMfaLoginResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/email/verify',
                VerifyMfaLoginResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaActivationRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        requestLoginMfaSmsSetup: () =>
            requestWithSchema<MfaActivationRequestResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/sms/request',
                MfaActivationRequestResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        verifyLoginMfaSmsSetup: (data: VerifyMfaActivationRequest) =>
            requestWithSchema<VerifyMfaLoginResponse>(
                '/portal/v1/user_management/any_user/login/mfa_setup/sms/verify',
                VerifyMfaLoginResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaActivationRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        getMfaStatus: dedupe(() =>
            requestWithSchema<MfaPolicyStatusResponse>(
                '/portal/v1/user_management/any_user/mfa_status',
                MfaPolicyStatusResponseSchema
            )
        ),

        requestEmailMfaActivation: () =>
            requestWithSchema<MfaActivationRequestResponse>(
                '/portal/v1/user_management/any_user/request_email_mfa_activation',
                MfaActivationRequestResponseSchema,
                {
                    method: 'POST',
                }
            ),

        verifyEmailMfaActivation: (data: VerifyMfaActivationRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/verify_email_mfa_activation',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaActivationRequestSchema.parse(data),
                }
            ),

        requestSmsMfaActivation: () =>
            requestWithSchema<MfaActivationRequestResponse>(
                '/portal/v1/user_management/any_user/request_sms_mfa_activation',
                MfaActivationRequestResponseSchema,
                {
                    method: 'POST',
                }
            ),

        verifySmsMfaActivation: (data: VerifyMfaActivationRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/verify_sms_mfa_activation',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: VerifyMfaActivationRequestSchema.parse(data),
                }
            ),

        disableMfa: () =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/disable_mfa',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        disableEmailMfa: () =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/disable_email_mfa',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        disableSmsMfa: () =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/disable_sms_mfa',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        logout: () =>
            requestWithSchema('/portal/v1/user_management/any_user/logout', MessageResponseSchema, {
                method: 'POST',
            }),

        swapCookieForToken: () =>
            requestWithSchema<SwapCookieForTokenResponse>(
                '/portal/v1/user_management/any_user/swap_cookie_for_token',
                SwapCookieForTokenResponseSchema,
                {
                    method: 'POST',
                }
            ),

        issueNativeAppLoginLink: () =>
            requestWithSchema<NativeAppLoginLinkResponse>(
                '/portal/v1/user_management/any_user/issue_native_app_login_link',
                NativeAppLoginLinkResponseSchema,
                {
                    method: 'POST',
                }
            ),

        cancelNativeAppLoginLink: (
            data: CancelNativeAppLoginLinkRequest,
            options: Pick<PillarApiRequestOptions, 'keepalive'> = {}
        ) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/cancel_native_app_login_link',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: CancelNativeAppLoginLinkRequestSchema.parse(data),
                    keepalive: options.keepalive,
                }
            ),

        swapTokenForCookie: (data: SwapTokenForCookieRequest) =>
            requestWithSchema<SwapTokenForCookieResponse>(
                '/portal/v1/user_management/any_user/swap_token_for_cookie',
                SwapTokenForCookieResponseSchema,
                {
                    method: 'POST',
                    body: SwapTokenForCookieRequestSchema.parse(data),
                    handle401AsSessionExpired: false,
                }
            ),

        getProfile: dedupe((options: GetProfileRequestOptions = {}) =>
            requestWithSchema<SessionUserProfileDetailedResponse>(
                '/portal/v1/user_management/any_user/profile',
                SessionUserProfileDetailedResponseSchema,
                options
            )
        ),

        getActiveRoleAssignment: dedupe(() =>
            requestWithSchema<ActiveRoleAssignmentResponse | null>(
                '/portal/v1/user_management/any_user/active_role_assignment',
                NullableActiveRoleAssignmentResponseSchema
            )
        ),

        requestSelfVerificationEmail: dedupe(() =>
            requestWithSchema<SendVerificationEmailResponse>(
                '/portal/v1/user_management/any_user/request_self_verification_email',
                SendVerificationEmailResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        verifyEmail: (token: string, password?: string) =>
            requestWithSchema<VerifyEmailResponse>(
                '/portal/v1/user_management/any_user/verify_email',
                VerifyEmailResponseSchema,
                {
                    method: 'POST',
                    body: {
                        token,
                        ...(password !== undefined ? { password } : {}),
                    } satisfies VerifyEmailRequest,
                    handle401AsSessionExpired: false,
                }
            ),

        createCustomerSelfOnboarding: (data: CreateCustomerSelfOnboardingRequest) =>
            requestWithSchema<CreateCustomerSelfOnboardingResponse>(
                '/portal/v1/user_management/any_user/customer_self_onboarding',
                CreateCustomerSelfOnboardingResponseSchema,
                {
                    method: 'POST',
                    body: data,
                    handle401AsSessionExpired: false,
                }
            ),

        requestPasswordReset: dedupe((email: string) =>
            requestWithSchema<RequestPasswordResetResponse>(
                '/portal/v1/user_management/any_user/request_password_reset',
                RequestPasswordResetResponseSchema,
                {
                    method: 'POST',
                    body: { email } satisfies RequestPasswordResetRequest,
                }
            )
        ),

        validatePasswordResetToken: (token: string) =>
            requestWithSchema<ValidatePasswordResetTokenResponse>(
                '/portal/v1/user_management/any_user/validate_reset_password_token',
                ValidatePasswordResetTokenResponseSchema,
                {
                    method: 'POST',
                    body: { token } satisfies ValidatePasswordResetTokenRequest,
                    handle401AsSessionExpired: false,
                }
            ),

        resetPassword: (token: string, newPassword: string) =>
            requestWithSchema<ResetPasswordResponse>(
                '/portal/v1/user_management/any_user/reset_password',
                ResetPasswordResponseSchema,
                {
                    method: 'POST',
                    body: { token, newPassword } satisfies ResetPasswordRequest,
                    handle401AsSessionExpired: false,
                }
            ),

        setDefaultRoleAssignment: (role_assignment_uuid: string) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/set_default_role_assignment',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: {
                        role_assignment_uuid,
                    } satisfies SetDefaultRoleAssignmentUuidRequest,
                }
            ),

        setActiveRoleAssignment: (role_assignment_uuid: string) =>
            requestWithSchema<SetActiveRoleAssignmentResponse>(
                '/portal/v1/user_management/any_user/set_active_role_assignment',
                SetActiveRoleAssignmentResponseSchema,
                {
                    method: 'POST',
                    body: {
                        role_assignment_uuid,
                    } satisfies SetActiveRoleAssignmentUuidRequest,
                }
            ),

        startImpersonation: (role_assignment_uuid: string) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/start_impersonation',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: StartImpersonationRequestSchema.parse({
                        role_assignment_uuid,
                    }) satisfies StartImpersonationRequest,
                }
            ),

        stopImpersonation: () =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/stop_impersonation',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        getImpersonationContext: dedupe(() =>
            requestWithSchema<ImpersonationContextResponse | null>(
                '/portal/v1/user_management/any_user/impersonation_context',
                ImpersonationContextResponseSchema.nullable()
            )
        ),

        changePassword: (data: ChangePasswordRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/change_password',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        setEmails: (data: SetUserEmailsRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/set_emails',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        setPhones: (data: SetUserPhonesRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/set_phones',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: SetUserPhonesRequestSchema.parse(data),
                }
            ),

        requestAddEmailVerification: (data: RequestAddEmailVerificationRequest) =>
            requestWithSchema<RequestAddEmailVerificationResponse>(
                '/portal/v1/user_management/any_user/request_add_email_verification',
                RequestAddEmailVerificationResponseSchema,
                {
                    method: 'POST',
                    body: RequestAddEmailVerificationRequestSchema.parse(data),
                }
            ),

        requestAddPhoneVerification: (data: RequestAddPhoneVerificationRequest) =>
            requestWithSchema<RequestAddPhoneVerificationResponse>(
                '/portal/v1/user_management/any_user/request_add_phone_verification',
                RequestAddPhoneVerificationResponseSchema,
                {
                    method: 'POST',
                    body: RequestAddPhoneVerificationRequestSchema.parse(data),
                }
            ),

        verifyAddPhone: (data: VerifyAddPhoneRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/verify_add_phone',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: VerifyAddPhoneRequestSchema.parse(data),
                }
            ),

        setupTOTP: dedupe(() =>
            requestWithSchema<SetupTotpResponse>(
                '/portal/v1/user_management/any_user/setup_totp',
                SetupTotpResponseSchema
            )
        ),

        enableTOTP: (data: EnableTotpRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/enable_totp',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        disableTOTP: () =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/disable_totp',
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        generateBackupCodes: dedupe(() =>
            requestWithSchema<GenerateBackupCodesResponse>(
                '/portal/v1/user_management/any_user/generate_backup_codes',
                GenerateBackupCodesResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        getSessions: () =>
            requestWithSchema<GetSessionsResponse>(
                '/portal/v1/user_management/any_user/sessions',
                GetSessionsResponseSchema
            ),

        expireSession: (data: ExpireSessionUuidRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/any_user/expire_session',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: ExpireSessionUuidRequestSchema.parse(data),
                }
            ),

        getAllSessions: () =>
            requestWithSchema<GetSessionsResponse>(
                '/portal/v1/user_management/admin/sessions',
                GetSessionsResponseSchema
            ),

        listAllSessionsPage: (params: SessionsSearchRequest = {}) => {
            const requestBody: SessionsSearchRequest = SessionsSearchRequestSchema.parse(params);
            return requestWithSchema<ListSessionsPageResponse>(
                '/portal/v1/user_management/admin/sessions/search',
                ListSessionsPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        expireSessionAdmin: (data: ExpireUserSessionRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/admin/expire_session',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: ExpireUserSessionRequestSchema.parse(data),
                }
            ),

        setSessionConsoleAdmin: (data: UpdateSessionConsoleRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/admin/set_session_console',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: UpdateSessionConsoleRequestSchema.parse(data),
                }
            ),

        deleteMyAccount: (password: string) =>
            requestWithSchema<DeleteMyAccountResponse>(
                '/portal/v1/user_management/any_user',
                DeleteMyAccountResponseSchema,
                {
                    method: 'DELETE',
                    body: { password } satisfies DeleteMyAccountRequest,
                }
            ),
    };

    const userApi = {
        listSingleUseTokensAdmin: (request: SingleUseTokensSearchRequest) =>
            requestWithSchema<ListSingleUseTokensResponse>(
                '/portal/v1/user_management/pillar_admin/single_use_tokens/search',
                ListSingleUseTokensResponseSchema,
                {
                    method: 'POST',
                    body: SingleUseTokensSearchRequestSchema.parse(request),
                }
            ),

        listSingleUseTokensByUserAdmin: (userUuid: string, request: SingleUseTokensSearchRequest) =>
            requestWithSchema<ListSingleUseTokensResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/single_use_tokens/search`,
                ListSingleUseTokensResponseSchema,
                {
                    method: 'POST',
                    body: SingleUseTokensSearchRequestSchema.parse(request),
                }
            ),

        expireSingleUseTokenAdmin: (singleUseAuthTokenUuid: string) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/single_use_tokens/${singleUseAuthTokenUuid}/expire`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        resendSingleUseTokenAdmin: (singleUseAuthTokenUuid: string) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/single_use_tokens/${singleUseAuthTokenUuid}/resend`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        setUserEmailsAdmin: (userUuid: string, data: SetUserEmailsRequest) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/set_emails`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        setUserPhonesAdmin: (userUuid: string, data: SetUserPhonesRequest) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/set_phones`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: SetUserPhonesRequestSchema.parse(data),
                }
            ),

        requestAddEmailVerificationAdmin: (
            userUuid: string,
            data: RequestAddEmailVerificationRequest
        ) =>
            requestWithSchema<RequestAddEmailVerificationResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/request_add_email_verification`,
                RequestAddEmailVerificationResponseSchema,
                {
                    method: 'POST',
                    body: RequestAddEmailVerificationRequestSchema.parse(data),
                }
            ),

        requestAddPhoneVerificationAdmin: (
            userUuid: string,
            data: RequestAddPhoneVerificationRequest
        ) =>
            requestWithSchema<RequestAddPhoneVerificationResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/request_add_phone_verification`,
                RequestAddPhoneVerificationResponseSchema,
                {
                    method: 'POST',
                    body: RequestAddPhoneVerificationRequestSchema.parse(data),
                }
            ),

        verifyAddPhoneAdmin: (userUuid: string, data: VerifyAddPhoneRequest) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/verify_add_phone`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: VerifyAddPhoneRequestSchema.parse(data),
                }
            ),

        resetPasswordAdmin: dedupe((userUuid: string, newPassword: string) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/reset_password`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: ResetPasswordAdminRequestSchema.parse({
                        new_password: newPassword,
                    } satisfies ResetPasswordAdminRequest),
                }
            )
        ),

        requestPasswordResetAdmin: (userUuid: string, data: RequestPasswordResetAdminRequest) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/request_password_reset`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: RequestPasswordResetAdminRequestSchema.parse(data),
                }
            ),

        disableMfaAdmin: (userUuid: string) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/superadmin/user/${userUuid}/disable_mfa`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        setMfaRequiredAdmin: (userUuid: string, data: SetUserMfaRequiredRequest) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/superadmin/user/${userUuid}/mfa_required`,
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: SetUserMfaRequiredRequestSchema.parse(data),
                }
            ),

        sendVerificationEmailAdmin: dedupe((userUuid: string) =>
            requestWithSchema<SendVerificationEmailResponse>(
                `/portal/v1/user_management/pillar_admin/user/${userUuid}/send_verification_email`,
                SendVerificationEmailResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        getUser: (userUuid: string) =>
            requestWithSchema<UserProfileDetailedPublicResponse>(
                `/portal/v1/user_management/user/${userUuid}`,
                UserProfileDetailedPublicResponseSchema
            ),

        provisionOrganizationalUser: dedupe((data: ProvisionOrganizationalUserRequest) =>
            requestWithSchema<ProvisionOrganizationalUserResponse>(
                '/portal/v1/user_management/pillar_admin/create_ouser',
                ProvisionOrganizationalUserResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        provisionInternalUser: dedupe((data: ProvisionUserRequest) =>
            requestWithSchema<ProvisionInternalUserResponse>(
                '/portal/v1/user_management/superadmin/create_iuser',
                ProvisionInternalUserResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        updateUser: dedupe((userUuid: string, data: UpdateUserRequest) =>
            requestWithSchema<UpdateUserResponse>(
                `/portal/v1/user_management/pillar_admin/update_user/${userUuid}`,
                UpdateUserResponseSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            )
        ),

        deleteUser: (userUuid: string) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/superadmin/delete_user/${userUuid}`,
                MessageResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        undeleteUser: (userUuid: string) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/superadmin/undelete_user/${userUuid}`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        assignRole: (data: AssignRoleRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/pillar_admin/assign_role',
                MessageResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        revokeRole: (data: RevokeRoleRequest) =>
            requestWithSchema<MessageResponse>(
                '/portal/v1/user_management/pillar_admin/revoke_role',
                MessageResponseSchema,
                {
                    method: 'DELETE',
                    body: data,
                }
            ),

        updateRoleAssignment: (
            roleAssignmentUuid: string,
            data: Omit<UpdateRoleAssignmentRequest, 'role_assignment_uuid'>
        ) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/role_assignments/${roleAssignmentUuid}`,
                MessageResponseSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            ),

        setDefaultRoleAssignment: (userUuid: string, roleAssignmentUuid: string) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/pillar_admin/set_default_role_assignment/${userUuid}`,
                MessageResponseSchema,
                {
                    method: 'PATCH',
                    body: {
                        role_assignment_uuid: roleAssignmentUuid,
                    } satisfies SetDefaultRoleAssignmentRequest,
                }
            ),

        searchUsers: (params: UserOmnisearchPageRequest) => {
            const queryParams = new URLSearchParams();
            if (params.q !== undefined) {
                queryParams.set('q', params.q);
            }
            if (params.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params.cursor !== undefined) {
                queryParams.set('cursor', params.cursor.toString());
            }
            if (params.organization_uuid !== undefined) {
                queryParams.set('organization_uuid', params.organization_uuid);
            }
            return requestWithSchema<UserOmnisearchPageResponse>(
                `/portal/v1/user_management/users/search?${queryParams.toString()}`,
                UserOmnisearchPageResponseSchema
            );
        },

        listInternalUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            requestWithSchema<PublicUserListingPageResponse>(
                '/portal/v1/internal_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),

        listIsoUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            requestWithSchema<PublicUserListingPageResponse>(
                '/portal/v1/iso_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),

        listMerchantUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            requestWithSchema<PublicUserListingPageResponse>(
                '/portal/v1/merchant_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),

        listCustomerUsersPage: (params: PublicUserListingSearchRequest = {}) =>
            requestWithSchema<PublicUserListingPageResponse>(
                '/portal/v1/customer_users/search',
                PublicUserListingPageResponseSchema,
                {
                    method: 'POST',
                    body: PublicUserListingSearchRequestSchema.parse(params),
                }
            ),
    };

    const organizationApi = {
        searchOrganizations: (params: OrganizationOmnisearchSearchRequest) => {
            const requestBody: OrganizationOmnisearchSearchRequest =
                OrganizationOmnisearchSearchRequestSchema.parse(params);
            return requestWithSchema<OrganizationOmnisearchPageResponse>(
                '/portal/v1/user_management/organizations/search',
                OrganizationOmnisearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        listSubsidiaryIsosPage: (
            organizationUuid: string,
            params?: { limit?: number; cursor?: string }
        ) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            return requestWithSchema<ListSubsidiaryIsosPageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/subsidiary_isos?${queryParams.toString()}`,
                ListSubsidiaryIsosPageResponseSchema
            );
        },

        listSubsidiaryMerchantsPage: (
            organizationUuid: string,
            params?: { limit?: number; cursor?: string }
        ) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            return requestWithSchema<ListSubsidiaryMerchantsPageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/subsidiary_merchants?${queryParams.toString()}`,
                ListSubsidiaryMerchantsPageResponseSchema
            );
        },

        listDirectOrganizationUsersPage: (
            organizationUuid: string,
            params?: { limit?: number; cursor?: string; include_descendants?: boolean }
        ) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            if (params?.include_descendants !== undefined) {
                queryParams.set(
                    'include_descendants',
                    params.include_descendants ? 'true' : 'false'
                );
            }
            return requestWithSchema<ListDirectOrganizationUsersPageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/users?${queryParams.toString()}`,
                ListDirectOrganizationUsersPageResponseSchema
            );
        },

        previewOrganizationUsersCsvImport: (
            organizationUuid: string,
            body: PreviewOrganizationUsersCsvImportRequest
        ) =>
            requestWithSchema<PreviewOrganizationUsersCsvImportResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/users/import-csv/preview`,
                PreviewOrganizationUsersCsvImportResponseSchema,
                {
                    method: 'POST',
                    body: PreviewOrganizationUsersCsvImportRequestSchema.parse(body),
                }
            ),

        applyOrganizationUsersCsvImport: (
            organizationUuid: string,
            body: ApplyOrganizationUsersCsvImportRequest
        ) =>
            requestWithSchema<ApplyOrganizationUsersCsvImportResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/users/import-csv/apply`,
                ApplyOrganizationUsersCsvImportResponseSchema,
                {
                    method: 'POST',
                    body: ApplyOrganizationUsersCsvImportRequestSchema.parse(body),
                }
            ),

        listDocumentTemplates: (
            organizationUuid: string,
            params: ListOrganizationDocumentTemplatesRequest = {}
        ) => {
            const validatedParams = ListOrganizationDocumentTemplatesRequestSchema.parse(params);
            const queryParams = new URLSearchParams();
            if (validatedParams.show_deleted === true) {
                queryParams.set('show_deleted', 'true');
            }
            const serializedQuery = queryParams.toString();
            return requestWithSchema<ListOrganizationDocumentTemplatesResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates${
                    serializedQuery.length === 0 ? '' : `?${serializedQuery}`
                }`,
                ListOrganizationDocumentTemplatesResponseSchema
            );
        },

        validateDocumentTemplate: (
            organizationUuid: string,
            body: ValidateOrganizationDocumentTemplateRequest
        ) =>
            requestWithSchema<ValidateOrganizationDocumentTemplateResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates/validate`,
                ValidateOrganizationDocumentTemplateResponseSchema,
                {
                    method: 'POST',
                    body: ValidateOrganizationDocumentTemplateRequestSchema.parse(body),
                }
            ),

        createDocumentTemplate: (
            organizationUuid: string,
            body: CreateOrganizationDocumentTemplateRequest
        ) =>
            requestWithSchema<OrganizationDocumentTemplateOverrideDescriptor>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates`,
                OrganizationDocumentTemplateOverrideDescriptorSchema,
                {
                    method: 'POST',
                    body: CreateOrganizationDocumentTemplateRequestSchema.parse(body),
                }
            ),

        updateDocumentTemplate: (
            organizationUuid: string,
            documentTemplateUuid: string,
            body: UpdateOrganizationDocumentTemplateRequest
        ) =>
            requestWithSchema<OrganizationDocumentTemplateOverrideDescriptor>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates/${documentTemplateUuid}`,
                OrganizationDocumentTemplateOverrideDescriptorSchema,
                {
                    method: 'PATCH',
                    body: UpdateOrganizationDocumentTemplateRequestSchema.parse(body),
                }
            ),

        setDocumentTemplateActive: (
            organizationUuid: string,
            documentTemplateUuid: string,
            body: SetOrganizationDocumentTemplateActiveRequest
        ) =>
            requestWithSchema<OrganizationDocumentTemplateOverrideDescriptor>(
                `/portal/v1/user_management/organization/${organizationUuid}/document-templates/${documentTemplateUuid}/active`,
                OrganizationDocumentTemplateOverrideDescriptorSchema,
                {
                    method: 'PATCH',
                    body: SetOrganizationDocumentTemplateActiveRequestSchema.parse(body),
                }
            ),

        getOrganization: (organizationUuid: string) =>
            requestWithSchema<GetOrganizationResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}`,
                GetOrganizationResponseSchema
            ),

        createIso: dedupe((data: CreateIsoRequest) =>
            requestWithSchema<CreateIsoResponse>(
                '/portal/v1/user_management/pillar_admin/create_iso',
                CreateIsoResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        createMerchant: dedupe((data: CreateMerchantRequest) =>
            requestWithSchema<CreateMerchantResponse>(
                '/portal/v1/user_management/pillar_admin/create_merchant',
                CreateMerchantResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        assignMerchantToIso: (data: AssignMerchantToIsoRequest) =>
            requestWithSchema<AssignMerchantToIsoResponse>(
                '/portal/v1/user_management/pillar_admin/assign_merchant_to_iso',
                AssignMerchantToIsoResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        setOrganizationParent: (data: SetOrganizationParentRequest) =>
            requestWithSchema<SetOrganizationParentResponse>(
                '/portal/v1/user_management/pillar_admin/set_organization_parent',
                SetOrganizationParentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        updateOrganization: dedupe((organizationUuid: string, data: UpdateOrganizationRequest) =>
            requestWithSchema<UpdateOrganizationResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}`,
                UpdateOrganizationResponseSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            )
        ),

        deleteOrganization: (organizationUuid: string) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}`,
                MessageResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        undeleteOrganization: (organizationUuid: string) =>
            requestWithSchema<UpdateOrganizationResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/undelete`,
                UpdateOrganizationResponseSchema,
                {
                    method: 'POST',
                }
            ),

        exportMerchantBackup: (
            organizationUuid: string,
            request: ExportMerchantBackupRequest = {}
        ) => {
            const parsedRequest: ExportMerchantBackupRequest =
                ExportMerchantBackupRequestSchema.parse(request);
            const searchParams = new URLSearchParams();
            if (parsedRequest.mode !== undefined) {
                searchParams.set('mode', parsedRequest.mode);
            }
            const queryString = searchParams.toString();
            return requestWithSchema<ExportMerchantBackupResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/merchant-backup/export${
                    queryString.length === 0 ? '' : `?${queryString}`
                }`,
                ExportMerchantBackupResponseSchema
            );
        },

        getMerchantChecklist: (organizationUuid: string) =>
            requestWithSchema<GetMerchantChecklistResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/merchant-checklist`,
                GetMerchantChecklistResponseSchema
            ),

        getMerchantChecklistCheck: (
            organizationUuid: string,
            checkCode: MerchantChecklistCheckCode
        ) =>
            requestWithSchema<GetMerchantChecklistCheckResponse>(
                `/portal/v1/user_management/organization/${organizationUuid}/merchant-checklist/check/${checkCode}`,
                GetMerchantChecklistCheckResponseSchema
            ),

        restoreMerchantBackupDryRun: (payload: RestoreMerchantBackupDryRunRequest) =>
            requestWithSchema<RestoreMerchantBackupDryRunResponse>(
                '/portal/v1/user_management/merchant-backup/restore/dry-run',
                RestoreMerchantBackupDryRunResponseSchema,
                {
                    method: 'POST',
                    body: RestoreMerchantBackupDryRunRequestSchema.parse(payload),
                }
            ),

        restoreMerchantBackupApply: (payload: RestoreMerchantBackupApplyRequest) =>
            requestWithSchema<RestoreMerchantBackupApplyResponse>(
                '/portal/v1/user_management/merchant-backup/restore/apply',
                RestoreMerchantBackupApplyResponseSchema,
                {
                    method: 'POST',
                    body: RestoreMerchantBackupApplyRequestSchema.parse(payload),
                }
            ),
    };

    const customerApi = {
        listCustomerUsersPage: (params: CustomerUsersSearchRequest = {}) => {
            const requestBody: CustomerUsersSearchRequest =
                CustomerUsersSearchRequestSchema.parse(params);
            return requestWithSchema<CustomerUsersPageResponse>(
                '/portal/v1/user_management/customer-users/search',
                CustomerUsersPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        searchCustomers: (params: MerchantCustomerRecordSearchRequest = {}) => {
            const requestBody: MerchantCustomerRecordSearchRequest =
                MerchantCustomerRecordSearchRequestSchema.parse(params);
            return requestWithSchema<MerchantCustomerRecordSearchPageResponse>(
                '/portal/v1/user_management/customers/search',
                MerchantCustomerRecordSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        lookupIpwCustomer: (params: MerchantCustomerRecordIpwLookupRequest) => {
            const requestBody: MerchantCustomerRecordIpwLookupRequest =
                MerchantCustomerRecordIpwLookupRequestSchema.parse(params);
            return requestWithSchema<MerchantCustomerRecordIpwLookupResponse>(
                '/portal/v1/user_management/customers/ipw/lookup',
                MerchantCustomerRecordIpwLookupResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getCustomer: (merchantCustomerRecordUuid: string) =>
            requestWithSchema<MerchantCustomerRecordDetailsResponse>(
                `/portal/v1/user_management/customers/${merchantCustomerRecordUuid}`,
                MerchantCustomerRecordDetailsResponseSchema
            ),

        updateCustomer: dedupe(
            (merchantCustomerRecordUuid: string, data: UpdateMerchantCustomerRecordRequest) =>
                requestWithSchema<MerchantCustomerRecordDetailsResponse>(
                    `/portal/v1/user_management/customers/${merchantCustomerRecordUuid}`,
                    MerchantCustomerRecordDetailsResponseSchema,
                    {
                        method: 'PATCH',
                        body: UpdateMerchantCustomerRecordRequestSchema.parse(data),
                    }
                )
        ),

        upsertIpwCustomer: dedupe((data: UpsertIpwMerchantCustomerRecordRequest) =>
            requestWithSchema<UpsertIpwMerchantCustomerRecordResponse>(
                '/portal/v1/user_management/customers/ipw/upsert',
                UpsertIpwMerchantCustomerRecordResponseSchema,
                {
                    method: 'POST',
                    body: UpsertIpwMerchantCustomerRecordRequestSchema.parse(data),
                }
            )
        ),
    };

    const parseLogicalTerminalUuidPathSegment = (logicalTerminalUuid: string): string =>
        LogicalTerminalUuidParamSchema.parse(logicalTerminalUuid);

    const paymentsInfrastructureApi = {
        searchProcessors: (params: ProcessorSearchRequest = {}) => {
            const parseResult = ProcessorSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(`Invalid processor search request: ${parseResult.error.message}`);
            }
            return requestWithSchema<ProcessorSearchPageResponse>(
                '/portal/v1/payments_infrastructure/processors/search',
                ProcessorSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getProcessor: (processorName: string) =>
            requestWithSchema<GetProcessorResponse>(
                `/portal/v1/payments_infrastructure/processors/${encodeURIComponent(processorName)}`,
                GetProcessorResponseSchema
            ),

        createProcessor: dedupe((payload: CreateProcessorRequest) => {
            const parseResult = CreateProcessorRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(`Invalid create processor request: ${parseResult.error.message}`);
            }
            return requestWithSchema<GetProcessorResponse>(
                '/portal/v1/payments_infrastructure/processors',
                GetProcessorResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateProcessor: dedupe((processorName: string, payload: UpdateProcessorRequest) => {
            const parseResult = UpdateProcessorRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(`Invalid update processor request: ${parseResult.error.message}`);
            }
            return requestWithSchema<GetProcessorResponse>(
                `/portal/v1/payments_infrastructure/processors/${encodeURIComponent(processorName)}`,
                GetProcessorResponseSchema,
                { method: 'PATCH', body: parseResult.data }
            );
        }),

        searchMerchantAccounts: (params: MerchantAccountSearchRequest = {}) => {
            const parseResult = MerchantAccountSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid merchant account search request: ${parseResult.error.message}`
                );
            }
            return requestWithSchema<MerchantAccountSearchPageResponse>(
                '/portal/v1/payments_infrastructure/merchant_accounts/search',
                MerchantAccountSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getMerchantAccount: (merchantAccountUuid: string) =>
            requestWithSchema<GetMerchantAccountResponse>(
                `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                    merchantAccountUuid
                )}`,
                GetMerchantAccountResponseSchema
            ),

        createMerchantAccount: dedupe((payload: CreateMerchantAccountRequest) => {
            const parseResult = CreateMerchantAccountRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create merchant account request: ${parseResult.error.message}`
                );
            }
            return requestWithSchema<GetMerchantAccountResponse>(
                '/portal/v1/payments_infrastructure/merchant_accounts',
                GetMerchantAccountResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateMerchantAccount: dedupe(
            (merchantAccountUuid: string, payload: UpdateMerchantAccountRequest) => {
                const parseResult = UpdateMerchantAccountRequestSchema.safeParse(payload);
                if (!parseResult.success) {
                    throw new Error(
                        `Invalid update merchant account request: ${parseResult.error.message}`
                    );
                }
                return requestWithSchema<GetMerchantAccountResponse>(
                    `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                        merchantAccountUuid
                    )}`,
                    GetMerchantAccountResponseSchema,
                    { method: 'PATCH', body: parseResult.data }
                );
            }
        ),

        deleteMerchantAccount: (merchantAccountUuid: string) =>
            requestWithSchema<GetMerchantAccountResponse>(
                `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                    merchantAccountUuid
                )}`,
                GetMerchantAccountResponseSchema,
                { method: 'DELETE' }
            ),

        undeleteMerchantAccount: (merchantAccountUuid: string) =>
            requestWithSchema<GetMerchantAccountResponse>(
                `/portal/v1/payments_infrastructure/merchant_accounts/${MerchantAccountUuidParamSchema.parse(
                    merchantAccountUuid
                )}/undelete`,
                GetMerchantAccountResponseSchema,
                { method: 'POST' }
            ),

        searchLogicalTerminals: (params: LogicalTerminalSearchRequest = {}) => {
            const parseResult = LogicalTerminalSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid logical terminal search request: ${parseResult.error.message}`
                );
            }
            return requestWithSchema<LogicalTerminalSearchPageResponse>(
                '/portal/v1/payments_infrastructure/logical_terminals/search',
                LogicalTerminalSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        searchMerchantOrganizations: (params: MerchantOrganizationSearchRequest) => {
            const parseResult = MerchantOrganizationSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid merchant organization search request: ${parseResult.error.message}`
                );
            }
            return requestWithSchema<MerchantOrganizationSearchPageResponse>(
                '/portal/v1/payments_infrastructure/merchant_organizations/search',
                MerchantOrganizationSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getLogicalTerminal: (logicalTerminalUuid: string) =>
            requestWithSchema<GetLogicalTerminalResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}`,
                GetLogicalTerminalResponseSchema
            ),

        createLogicalTerminal: dedupe((payload: CreateLogicalTerminalRequest) => {
            const parseResult = CreateLogicalTerminalRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create logical terminal request: ${parseResult.error.message}`
                );
            }
            return requestWithSchema<GetLogicalTerminalResponse>(
                '/portal/v1/payments_infrastructure/logical_terminals',
                GetLogicalTerminalResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateLogicalTerminal: dedupe(
            (logicalTerminalUuid: string, payload: UpdateLogicalTerminalRequest) => {
                const parseResult = UpdateLogicalTerminalRequestSchema.safeParse(payload);
                if (!parseResult.success) {
                    throw new Error(
                        `Invalid update logical terminal request: ${parseResult.error.message}`
                    );
                }
                return requestWithSchema<GetLogicalTerminalResponse>(
                    `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                        logicalTerminalUuid
                    )}`,
                    GetLogicalTerminalResponseSchema,
                    { method: 'PATCH', body: parseResult.data }
                );
            }
        ),

        deleteLogicalTerminal: (logicalTerminalUuid: string) =>
            requestWithSchema<GetLogicalTerminalResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}`,
                GetLogicalTerminalResponseSchema,
                { method: 'DELETE' }
            ),

        undeleteLogicalTerminal: (logicalTerminalUuid: string) =>
            requestWithSchema<GetLogicalTerminalResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}/undelete`,
                GetLogicalTerminalResponseSchema,
                { method: 'POST' }
            ),

        previewLogicalTerminalOrganizationChange: (
            logicalTerminalUuid: string,
            payload: PreviewLogicalTerminalOrganizationChangeRequest
        ) => {
            const parseResult =
                PreviewLogicalTerminalOrganizationChangeRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid logical terminal organization preview request: ${parseResult.error.message}`
                );
            }
            return requestWithSchema<PreviewLogicalTerminalOrganizationChangeResponse>(
                `/portal/v1/payments_infrastructure/logical_terminals/${parseLogicalTerminalUuidPathSegment(
                    logicalTerminalUuid
                )}/organization-change-preview`,
                PreviewLogicalTerminalOrganizationChangeResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        searchTerminalDevices: (params: TerminalDeviceSearchRequest = {}) => {
            const parseResult = TerminalDeviceSearchRequestSchema.safeParse(params);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid terminal device search request: ${parseResult.error.message}`
                );
            }
            return requestWithSchema<TerminalDeviceSearchPageResponse>(
                '/portal/v1/payments_infrastructure/terminal_devices/search',
                TerminalDeviceSearchPageResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        },

        getTerminalDevice: (terminalDeviceUuid: string) =>
            requestWithSchema<GetTerminalDeviceResponse>(
                `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}`,
                GetTerminalDeviceResponseSchema
            ),

        getCloverReadiness: (merchantOrganizationUuid: string, terminalDeviceUuid: string) =>
            requestWithSchema<CloverReadinessResult>(
                `/portal/v1/payments_infrastructure/merchant_organizations/${OrganizationUuidParamSchema.parse(
                    merchantOrganizationUuid
                )}/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}/clover/readiness`,
                CloverReadinessResultSchema
            ),

        getCloverOAuthAuthorizationStatus: (merchantOrganizationUuid: string) =>
            requestWithSchema<CloverOAuthAuthorizationStatus>(
                `/clover/oauth/status?${new URLSearchParams({
                    organization_uuid: OrganizationUuidParamSchema.parse(
                        merchantOrganizationUuid
                    ),
                }).toString()}`,
                CloverOAuthAuthorizationStatusSchema
            ),

        startCloverOAuthReauthorization: (merchantOrganizationUuid: string) => {
            const request = StartCloverOAuthReauthorizationRequestSchema.parse({
                organization_uuid: merchantOrganizationUuid,
            });
            return requestWithSchema<StartCloverOAuthReauthorizationResponse>(
                '/clover/oauth/reauthorize',
                StartCloverOAuthReauthorizationResponseSchema,
                { method: 'POST', body: request }
            );
        },

        invalidateCloverOAuthAuthorization: (merchantOrganizationUuid: string) =>
            requestWithSchema<CloverOAuthAuthorizationStatus>(
                '/clover/oauth/invalidate',
                CloverOAuthAuthorizationStatusSchema,
                {
                    method: 'POST',
                    body: {
                        organization_uuid: OrganizationUuidParamSchema.parse(
                            merchantOrganizationUuid
                        ),
                    },
                }
            ),

        createTerminalDevice: dedupe((payload: CreateTerminalDeviceRequest) => {
            const parseResult = CreateTerminalDeviceRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create terminal device request: ${parseResult.error.message}`
                );
            }
            return requestWithSchema<GetTerminalDeviceResponse>(
                '/portal/v1/payments_infrastructure/terminal_devices',
                GetTerminalDeviceResponseSchema,
                { method: 'POST', body: parseResult.data }
            );
        }),

        updateTerminalDevice: dedupe(
            (terminalDeviceUuid: string, payload: UpdateTerminalDeviceRequest) => {
                const parseResult = UpdateTerminalDeviceRequestSchema.safeParse(payload);
                if (!parseResult.success) {
                    throw new Error(
                        `Invalid update terminal device request: ${parseResult.error.message}`
                    );
                }
                return requestWithSchema<GetTerminalDeviceResponse>(
                    `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                        terminalDeviceUuid
                    )}`,
                    GetTerminalDeviceResponseSchema,
                    { method: 'PATCH', body: parseResult.data }
                );
            }
        ),

        deleteTerminalDevice: (terminalDeviceUuid: string) =>
            requestWithSchema<GetTerminalDeviceResponse>(
                `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}`,
                GetTerminalDeviceResponseSchema,
                { method: 'DELETE' }
            ),

        undeleteTerminalDevice: (terminalDeviceUuid: string) =>
            requestWithSchema<GetTerminalDeviceResponse>(
                `/portal/v1/payments_infrastructure/terminal_devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}/undelete`,
                GetTerminalDeviceResponseSchema,
                { method: 'POST' }
            ),
    };

    const sambaConfigApi = {
        getIsoAdminRoleAssignmentConfig: (roleAssignmentUuid: string) =>
            requestWithSchema<RoleAssignmentCustomConfig>(
                `/portal/v1/samba_config/iso_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                RoleAssignmentUserConfigResponseSchema
            ),

        getIsoAdminRoleAssignmentOrgConfig: (roleAssignmentUuid: string) =>
            requestWithSchema<MerchantConfig>(
                `/portal/v1/samba_config/iso_admin/role_assignment/${roleAssignmentUuid}/org_config`,
                ConfigResponseSchema
            ),

        updateIsoAdminRoleAssignmentConfig: (
            roleAssignmentUuid: string,
            config: RoleAssignmentCustomConfig,
            expectedConfig: RoleAssignmentCustomConfig
        ) =>
            requestWithSchema<RoleAssignmentCustomConfig>(
                `/portal/v1/samba_config/iso_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                RoleAssignmentUserConfigResponseSchema,
                {
                    method: 'PUT',
                    body: UpdateRoleAssignmentConfigRequestSchema.parse({
                        expected_config: expectedConfig,
                        config,
                    } satisfies UpdateRoleAssignmentConfigRequest),
                }
            ),

        getIsoAdminOrgConfig: (organizationUuid: string) =>
            requestWithSchema<MerchantConfig>(
                `/portal/v1/samba_config/iso_admin/org_config/${organizationUuid}`,
                ConfigResponseSchema
            ),

        getRoleAssignmentConfig: (roleAssignmentUuid: string) =>
            requestWithSchema<RoleAssignmentCustomConfig>(
                `/portal/v1/samba_config/pillar_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                RoleAssignmentUserConfigResponseSchema
            ),

        updateRoleAssignmentConfig: (
            roleAssignmentUuid: string,
            config: RoleAssignmentCustomConfig,
            expectedConfig: RoleAssignmentCustomConfig
        ) =>
            requestWithSchema<RoleAssignmentCustomConfig>(
                `/portal/v1/samba_config/pillar_admin/role_assignment/${roleAssignmentUuid}/user_config`,
                RoleAssignmentUserConfigResponseSchema,
                {
                    method: 'PUT',
                    body: UpdateRoleAssignmentConfigRequestSchema.parse({
                        expected_config: expectedConfig,
                        config,
                    } satisfies UpdateRoleAssignmentConfigRequest),
                }
            ),

        getOrgConfig: (organizationUuid: string) =>
            requestWithSchema<MerchantConfig>(
                `/portal/v1/samba_config/pillar_admin/org_config/${organizationUuid}`,
                ConfigResponseSchema
            ),

        getCloverRuntimeEnvironmentAvailability: (organizationUuid: string) =>
            requestWithSchema<CloverRuntimeEnvironmentAvailabilityResponse>(
                `/portal/v1/samba_config/pillar_admin/org_config/${organizationUuid}/clover_runtime_availability`,
                CloverRuntimeEnvironmentAvailabilityResponseSchema
            ),

        getMyUserConfig: () =>
            requestWithSchema<RoleAssignmentCustomConfig>(
                '/portal/v1/samba_config/any_user/user',
                RoleAssignmentUserConfigResponseSchema
            ),

        getMyOrgConfig: () =>
            requestWithSchema<MerchantConfig>(
                '/portal/v1/samba_config/any_user/org',
                ConfigResponseSchema
            ),

        getMyEffectiveConfig: () =>
            requestWithSchema<EffectiveConfigResponse>(
                '/portal/v2/samba_config/any_user/effective',
                EffectiveConfigResponseSchema
            ),

        getMyNavigateMenuWebsites: () =>
            requestWithSchema<NavigateMenuWebsitesResponse>(
                '/portal/v1/samba_config/any_user/navigate_menu',
                NavigateMenuWebsitesResponseSchema
            ),

        getMyCombinedConfig: () =>
            requestWithSchema<CombinedConfigResponse>(
                '/portal/v1/samba_config/any_user/combined',
                CombinedConfigResponseSchema
            ),

        getMyRoleAssignmentUserConfig: (roleAssignmentUuid: string) =>
            requestWithSchema<RoleAssignmentCustomConfig>(
                `/portal/v1/samba_config/any_user/role_assignment/${roleAssignmentUuid}/user_config`,
                RoleAssignmentUserConfigResponseSchema
            ),

        getMyRoleAssignmentOrgConfig: (roleAssignmentUuid: string) =>
            requestWithSchema<MerchantConfig>(
                `/portal/v1/samba_config/any_user/role_assignment/${roleAssignmentUuid}/org_config`,
                ConfigResponseSchema
            ),

        patchMyActiveRoleAssignmentUserRoleAssignmentConfig: (
            request: PatchActiveRoleAssignmentUserRoleAssignmentConfigRequest
        ) =>
            requestWithSchema<RoleAssignmentCustomConfig>(
                '/portal/v1/samba_config/any_user/active_role_assignment/user_role_assignment_config',
                RoleAssignmentUserConfigResponseSchema,
                {
                    method: 'PATCH',
                    body: PatchActiveRoleAssignmentUserRoleAssignmentConfigRequestSchema.parse(
                        request
                    ),
                }
            ),

        getCombinedConfigForUser: (userId: number) =>
            requestWithSchema<CombinedConfigResponse>(
                `/portal/v1/samba_config/pillar_admin/effective_config/${userId}`,
                CombinedConfigResponseSchema
            ),

        getCombinedConfigForSessionOther: (sessionUuid: string) =>
            requestWithSchema<CombinedConfigResponse>(
                `/portal/v1/samba_config/admin/session/${sessionUuid}/config/combined`,
                CombinedConfigResponseSchema
            ),

        getCombinedConfigForSessionSelf: (auditId: number) =>
            requestWithSchema<CombinedConfigResponse>(
                `/portal/v1/samba_config/any_user/session/${auditId}/config/combined`,
                CombinedConfigResponseSchema
            ),
    };

    const paymentsApi = {
        processOutstandingCloverWebhooks: () =>
            requestWithSchema<CloverWebhookProcessingResult>(
                '/portal/v1/internal/clover-webhooks/process',
                CloverWebhookProcessingResultSchema,
                { method: 'POST' }
            ),

        getVaultTransactions: (params: GetVaultTransactionsSearchRequest = {}) => {
            const requestBody: GetVaultTransactionsSearchRequest =
                GetVaultTransactionsSearchRequestSchema.parse(params);
            return requestWithSchema<GetVaultTransactionsResponse>(
                '/portal/v1/payments/vault/transactions/search',
                GetVaultTransactionsResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        listPaymentTransactionLifecycleEvents: (
            params: PaymentTransactionLifecycleEventSearchRequest = {}
        ) => {
            const requestBody: PaymentTransactionLifecycleEventSearchRequest =
                PaymentTransactionLifecycleEventSearchRequestSchema.parse(params);
            return requestWithSchema<PaymentTransactionLifecycleEventListResponse>(
                '/portal/v1/payments/transaction-lifecycle-events/search',
                PaymentTransactionLifecycleEventListResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getPaymentAuditLog: (params: GetPaymentAuditLogSearchRequest) => {
            const requestBody: GetPaymentAuditLogSearchRequest =
                GetPaymentAuditLogSearchRequestSchema.parse(params);
            return requestWithSchema<GetPaymentAuditLogResponse>(
                '/portal/v1/payments/audit-log/search',
                GetPaymentAuditLogResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getSavedCardPaymentMethods: (params: GetSavedCardPaymentMethodsRequest) => {
            const request: GetSavedCardPaymentMethodsRequest =
                GetSavedCardPaymentMethodsRequestSchema.parse(params);
            const queryParams = new URLSearchParams({
                customer_name: request.customer_name,
                phone_number: request.phone_number,
                ipw_system_id: request.ipw_system_id.toString(),
                terminal_device_uuid: request.terminal_device_uuid,
            });
            return requestWithSchema<GetSavedCardPaymentMethodsResponse>(
                `/portal/v1/payments/method/saved-card?${queryParams.toString()}`,
                GetSavedCardPaymentMethodsResponseSchema
            );
        },

        getDelegateToken: dedupe((data: GetDelegateTokenRequest) => {
            const requestBody: GetDelegateTokenRequest = GetDelegateTokenRequestSchema.parse(data);
            return requestWithSchema<GetDelegateTokenResponse>(
                '/portal/v1/payments/delegate-token',
                GetDelegateTokenResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        }),

        binLookup: dedupe((data: BinLookupRequest) => {
            const requestBody: BinLookupRequest = BinLookupRequestSchema.parse(data);
            return requestWithSchema<BinLookupResponse>(
                '/portal/v1/payments/bin-lookup',
                BinLookupResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        }),

        getPaymentConfiguration: (params: GetPaymentConfigurationRequest) => {
            const validatedParams: GetPaymentConfigurationRequest =
                GetPaymentConfigurationRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                terminal_device_uuid: validatedParams.terminal_device_uuid,
            });
            return requestWithSchema<PaymentConfigurationResponse>(
                `/portal/v1/payments/payment-configuration?${queryParams.toString()}`,
                PaymentConfigurationResponseSchema
            );
        },

        getPaymentActionReadiness: (terminalDeviceUuid: string) =>
            requestWithSchema<PaymentActionReadinessResponse>(
                `/portal/v1/payments/terminal-devices/${TerminalDeviceUuidParamSchema.parse(
                    terminalDeviceUuid
                )}/action-readiness`,
                PaymentActionReadinessResponseSchema
            ),

        createDraftPaymentRequest: (data: CreateDraftPaymentRequestRequest) => {
            const validatedData: CreateDraftPaymentRequestRequest =
                CreateDraftPaymentRequestRequestSchema.parse(data);
            return requestWithSchema<CreateDraftPaymentRequestResponse>(
                '/portal/v1/payments/payment-requests/draft',
                CreateDraftPaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        getPaymentRequestOwnerCandidates: () =>
            requestWithSchema<PaymentRequestOwnerCandidatesResponse>(
                '/portal/v1/payments/payment-requests/owner-candidates',
                PaymentRequestOwnerCandidatesResponseSchema
            ),

        uploadDraftPaymentRequestDocument: (paymentRequestUuid: string, file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            return requestWithSchema<UploadPaymentRequestDocumentResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/documents/upload`,
                UploadPaymentRequestDocumentResponseSchema,
                {
                    method: 'POST',
                    body: formData,
                }
            );
        },

        finalizeSendPaymentRequest: (data: FinalizeSendPaymentRequestRequest) => {
            const validatedData: FinalizeSendPaymentRequestRequest =
                FinalizeSendPaymentRequestRequestSchema.parse(data);
            return requestWithSchema<FinalizeSendPaymentRequestResponse>(
                '/portal/v1/payments/payment-requests/finalize-send',
                FinalizeSendPaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        listMerchantPaymentRequests: (params: MerchantPaymentRequestSearchRequest = {}) => {
            const requestBody: MerchantPaymentRequestSearchRequest =
                MerchantPaymentRequestSearchRequestSchema.parse(params);
            return requestWithSchema<MerchantPaymentRequestListResponse>(
                '/portal/v1/payments/payment-requests/search',
                MerchantPaymentRequestListResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getMerchantPaymentRequestDetails: (
            paymentRequestUuid: string,
            params: MerchantPaymentRequestDetailsRequest = {}
        ) => {
            const validatedParams: MerchantPaymentRequestDetailsRequest =
                MerchantPaymentRequestDetailsRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/payment-requests/${paymentRequestUuid}?${querySuffix}`
                    : `/portal/v1/payments/payment-requests/${paymentRequestUuid}`;
            return requestWithSchema<PaymentRequestDetailsView>(
                endpoint,
                PaymentRequestDetailsViewSchema
            );
        },

        getMerchantPaymentRequestDeliveryEvents: (paymentRequestUuid: string) =>
            requestWithSchema<PaymentRequestDeliveryEventList>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/delivery-events`,
                PaymentRequestDeliveryEventListSchema
            ),

        resendPaymentRequest: (paymentRequestUuid: string, data: ResendPaymentRequestRequest) => {
            const validatedData: ResendPaymentRequestRequest =
                ResendPaymentRequestRequestSchema.parse(data);
            return requestWithSchema<FinalizeSendPaymentRequestResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/resend`,
                FinalizeSendPaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        cancelAndReissuePaymentRequest: (
            paymentRequestUuid: string,
            data: CancelAndReissuePaymentRequestRequest
        ) => {
            const validatedData: CancelAndReissuePaymentRequestRequest =
                CancelAndReissuePaymentRequestRequestSchema.parse(data);
            return requestWithSchema<CancelAndReissuePaymentRequestResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/cancel-and-reissue`,
                CancelAndReissuePaymentRequestResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                }
            );
        },

        revokePaymentRequestLink: (
            paymentRequestUuid: string,
            params: MerchantPaymentRequestDetailsRequest = {}
        ) => {
            const validatedParams: MerchantPaymentRequestDetailsRequest =
                MerchantPaymentRequestDetailsRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/payment-requests/${paymentRequestUuid}/revoke-payment-link?${querySuffix}`
                    : `/portal/v1/payments/payment-requests/${paymentRequestUuid}/revoke-payment-link`;
            return requestWithSchema<MessageResponse>(endpoint, MessageResponseSchema, {
                method: 'POST',
            });
        },

        bootstrapPaymentRequestGuestSession: (tokenValue: string) =>
            requestWithSchema<PublicPaymentRequestView>(
                `/portal/v1/payments/public/payment-request-links/${tokenValue}/bootstrap`,
                PublicPaymentRequestViewSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentPublicPaymentRequest: () =>
            requestWithSchema<PublicPaymentRequestView>(
                '/portal/v1/payments/public/payment-request-links/current',
                PublicPaymentRequestViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        requestCurrentPaymentRequestReplacementLink: () =>
            requestWithSchema<PublicPaymentLinkActionResponse>(
                '/portal/v1/payments/public/payment-request-links/current/request-new-link',
                PublicPaymentLinkActionResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        resendCurrentReissuedPaymentRequestLink: () =>
            requestWithSchema<PublicPaymentLinkActionResponse>(
                '/portal/v1/payments/public/payment-request-links/current/resend-reissued-link',
                PublicPaymentLinkActionResponseSchema,
                {
                    method: 'POST',
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestPaymentEntryContext: () =>
            requestWithSchema<GuestPaymentEntryContextView>(
                '/portal/v1/payments/public/payment-request-links/current/payment-entry-context',
                GuestPaymentEntryContextViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentAuthenticatedPaymentEntryContext: () =>
            requestWithSchema<AuthenticatedPaymentEntryContextView>(
                '/portal/v1/payments/authenticated/payment-requests/current/payment-entry-context',
                AuthenticatedPaymentEntryContextViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestPaymentSuccessView: () =>
            requestWithSchema<GuestPaymentSuccessView>(
                '/portal/v1/payments/public/payment-request-links/current/payment-success',
                GuestPaymentSuccessViewSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestReceiptHTML: () =>
            requestTextWithSchema(
                '/portal/v1/payments/public/payment-request-links/current/receipt-html',
                z.string().min(1),
                {
                    handle401AsSessionExpired: false,
                }
            ),

        getCurrentGuestPaymentRequestDocumentAccess: async (
            paymentRequestDocumentUuid: string
        ): Promise<GuestPaymentRequestDocumentAccessResponse> => {
            const validatedPaymentRequestDocumentUuid: string = z
                .string()
                .uuid()
                .parse(paymentRequestDocumentUuid);
            const endpoint: string =
                '/portal/v1/payments/public/payment-request-links/documents/' +
                `${validatedPaymentRequestDocumentUuid}/signed-url`;
            const response: Response = await fetchImplementation(buildRequestUrl(endpoint), {
                method: 'GET',
                credentials: 'include',
                headers: getRequestHeaders ? getRequestHeaders() : {},
                redirect: 'manual',
            });

            if (onResponse) {
                onResponse(response);
            }

            if (response.status === 401) {
                const errorMessage: string = response.statusText || 'Unauthorized';
                throw new ApiError(response.status, response.statusText, errorMessage);
            }

            if (response.status >= 300 && response.status < 400) {
                const locationHeaderValue: string | null = response.headers.get('location');
                if (locationHeaderValue === null || locationHeaderValue.trim().length === 0) {
                    throw new ApiError(
                        response.status,
                        response.statusText,
                        'Redirect response missing Location header'
                    );
                }
                await response.arrayBuffer();
                return GuestPaymentRequestDocumentAccessResponseSchema.parse({
                    kind: 'redirect',
                    url: new URL(locationHeaderValue, buildRequestUrl(endpoint)).toString(),
                });
            }

            if (!response.ok) {
                const errorText: string = await response.text();
                const errorMessage: string =
                    errorText.trim().length > 0 ? errorText : response.statusText;
                throw new ApiError(response.status, response.statusText, errorMessage);
            }

            const contentType: string | null = response.headers.get('content-type');
            if (contentType === null || contentType.trim().length === 0) {
                throw new ApiError(
                    response.status,
                    response.statusText,
                    'Inline document response missing Content-Type header'
                );
            }

            const contentLengthHeaderValue: string | null = response.headers.get('content-length');
            const contentLength: number | undefined =
                contentLengthHeaderValue === null
                    ? undefined
                    : z.coerce.number().int().nonnegative().parse(contentLengthHeaderValue);

            await response.arrayBuffer();
            return GuestPaymentRequestDocumentAccessResponseSchema.parse({
                kind: 'inline',
                content_type: contentType,
                content_length: contentLength,
            });
        },

        getCurrentGuestDelegateToken: () =>
            requestWithSchema<GetDelegateTokenResponse>(
                '/portal/v1/payments/public/payment-request-links/current/delegate-token',
                GetDelegateTokenResponseSchema,
                {
                    handle401AsSessionExpired: false,
                }
            ),

        guestBinLookup: (data: GuestBinLookupRequest) => {
            const validatedData: GuestBinLookupRequest = GuestBinLookupRequestSchema.parse(data);
            return requestWithSchema<BinLookupResponse>(
                '/portal/v1/payments/public/payment-request-links/current/bin-lookup',
                BinLookupResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        processCurrentGuestCardTokenPayment: (data: GuestProcessCardTokenPaymentRequest) => {
            const validatedData: GuestProcessCardTokenPaymentRequest =
                GuestProcessCardTokenPaymentRequestSchema.parse(data);
            return requestWithSchema<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/public/payment-request-links/current/payment/card-token',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        processCurrentAuthenticatedCardTokenPayment: (
            data: AuthenticatedProcessCardTokenPaymentRequest
        ) => {
            const validatedData: AuthenticatedProcessCardTokenPaymentRequest =
                AuthenticatedProcessCardTokenPaymentRequestSchema.parse(data);
            return requestWithSchema<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/authenticated/payment-requests/current/payment/card-token',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        processCurrentStoredPaymentMethodPayment: (
            data: GuestProcessStoredPaymentMethodPaymentRequest
        ) => {
            const validatedData: GuestProcessStoredPaymentMethodPaymentRequest =
                GuestProcessStoredPaymentMethodPaymentRequestSchema.parse(data);
            return requestWithSchema<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/authenticated/payment-requests/current/payment/stored-method',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: validatedData,
                    handle401AsSessionExpired: false,
                }
            );
        },

        listCardPaymentTerminalDevices: (params: ListCardPaymentTerminalDevicesRequest) => {
            const validatedParams: ListCardPaymentTerminalDevicesRequest =
                ListCardPaymentTerminalDevicesRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                is_virtual: String(validatedParams.is_virtual),
                action: validatedParams.action,
            });
            if (validatedParams.system_id !== undefined) {
                queryParams.set('system_id', String(validatedParams.system_id));
            }
            if (validatedParams.resource_type !== undefined) {
                queryParams.set('resource_type', validatedParams.resource_type);
            }
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            if (validatedParams.include_empty_reason !== undefined) {
                queryParams.set(
                    'include_empty_reason',
                    String(validatedParams.include_empty_reason)
                );
            }
            return requestWithSchema<ListCardPaymentTerminalDevicesResponse>(
                `/portal/v1/payments/card-payment/terminals?${queryParams.toString()}`,
                ListCardPaymentTerminalDevicesResponseSchema
            );
        },

        getAccessibleCredentialsInfo: (params: ListAccessibleCredentialsInfoRequest) => {
            const validatedParams: ListAccessibleCredentialsInfoRequest =
                ListAccessibleCredentialsInfoRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                is_virtual: String(validatedParams.is_virtual),
            });
            if (validatedParams.system_id !== undefined) {
                queryParams.set('system_id', String(validatedParams.system_id));
            }
            if (validatedParams.credential_type !== undefined) {
                queryParams.set('credential_type', validatedParams.credential_type);
            }
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            return requestWithSchema<ListAccessibleCredentialsInfoResponse>(
                `/portal/v1/payments/info/accessible-credentials?${queryParams.toString()}`,
                ListAccessibleCredentialsInfoResponseSchema
            );
        },

        getAccessibleTerminalsInfo: (params: ListAccessibleTerminalsInfoRequest) => {
            const validatedParams: ListAccessibleTerminalsInfoRequest =
                ListAccessibleTerminalsInfoRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams({
                is_virtual: String(validatedParams.is_virtual),
            });
            if (validatedParams.system_id !== undefined) {
                queryParams.set('system_id', String(validatedParams.system_id));
            }
            if (validatedParams.action !== undefined) {
                queryParams.set('action', validatedParams.action);
            }
            if (validatedParams.resource_type !== undefined) {
                queryParams.set('resource_type', validatedParams.resource_type);
            }
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            return requestWithSchema<ListAccessibleTerminalsInfoResponse>(
                `/portal/v1/payments/info/accessible-terminals?${queryParams.toString()}`,
                ListAccessibleTerminalsInfoResponseSchema
            );
        },

        getReconcilePaymentsCandidates: (params: GetReconcilePaymentsCandidatesRequest) => {
            const queryParams = new URLSearchParams({
                ipw_system_id: params.ipw_system_id.toString(),
            });
            if ('payment_transaction_uuid' in params) {
                queryParams.set('payment_transaction_uuid', params.payment_transaction_uuid);
            } else {
                queryParams.set('customer_name', params.customer_name);
                if (params.phone_number !== undefined) {
                    queryParams.set('phone_number', params.phone_number);
                }
                if (params.parts_invoice_number !== undefined) {
                    queryParams.set('parts_invoice_number', params.parts_invoice_number);
                }
                if (params.ro_number !== undefined) {
                    queryParams.set('ro_number', params.ro_number);
                }
            }
            if (params.reconciled !== undefined) {
                queryParams.set('reconciled', params.reconciled);
            }
            if (params.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            return requestWithSchema<GetReconcilePaymentsCandidatesResponse>(
                `/portal/v1/payments/reconcile/candidates?${queryParams.toString()}`,
                GetReconcilePaymentsCandidatesResponseSchema
            );
        },

        getTransactionDetails: (paymentTransactionUuid: string) =>
            requestWithSchema<GetTransactionDetailsResponse>(
                `/portal/v1/payments/vault/transactions/${paymentTransactionUuid}`,
                GetTransactionDetailsResponseSchema
            ),

        refreshGatewayTransactionDetails: dedupe((paymentTransactionUuid: string) => {
            const validatedPaymentTransactionUuid: string =
                PaymentTransactionUuidParamSchema.parse(paymentTransactionUuid);
            return requestWithSchema<GatewayTransactionDetailsFetchResult>(
                `/portal/v1/payments/vault/transactions/${encodeURIComponent(
                    validatedPaymentTransactionUuid
                )}/gateway-details/refresh`,
                GatewayTransactionDetailsFetchResultSchema,
                {
                    method: 'POST',
                }
            );
        }),

        getReceiptData: (paymentTransactionUuid: string) => {
            const validatedPaymentTransactionUuid: string =
                PaymentTransactionUuidParamSchema.parse(paymentTransactionUuid);
            return requestWithSchema<GetReceiptDataResponse>(
                `/portal/v1/payments/vault/receipt-data/${validatedPaymentTransactionUuid}`,
                GetReceiptDataResponseSchema
            );
        },

        getReceiptHTML: (paymentTransactionUuid: string) =>
            requestTextWithSchema(
                `/portal/v1/payments/vault/receipt-html/${paymentTransactionUuid}`,
                z.string().min(1)
            ),

        getReceiptHTMLUrl: (paymentTransactionUuid: string): string => {
            const endpoint = `/portal/v1/payments/vault/receipt-html/${paymentTransactionUuid}`;
            return buildRequestUrl(endpoint).toString();
        },

        refundTransaction: dedupe((data: RefundTransactionRequest) =>
            requestWithSchema<RefundTransactionResponse>(
                '/portal/v1/payments/refund',
                RefundTransactionResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        refundPaymentRequest: dedupe(
            (paymentRequestUuid: string, data: RefundPaymentRequestRequest) =>
                requestWithSchema<RefundPaymentRequestResponse>(
                    `/portal/v1/payments/payment-requests/${paymentRequestUuid}/refund`,
                    RefundPaymentRequestResponseSchema,
                    {
                        method: 'POST',
                        body: data,
                    }
                )
        ),

        resetTransactionReconciliation: dedupe((paymentTransactionUuid: string) =>
            requestWithSchema<ResetTransactionReconciliationResponse>(
                `/portal/v1/payments/vault/transactions/${paymentTransactionUuid}/reset-reconciliation`,
                ResetTransactionReconciliationResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        resetPaymentRequestReconciliation: dedupe((paymentRequestUuid: string) =>
            requestWithSchema<ResetPaymentRequestReconciliationResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/reset-reconciliation`,
                ResetPaymentRequestReconciliationResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        voidTransaction: dedupe((data: VoidTransactionRequest) =>
            requestWithSchema<VoidTransactionResponse>(
                '/portal/v1/payments/void',
                VoidTransactionResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        voidPaymentRequest: dedupe((paymentRequestUuid: string) =>
            requestWithSchema<VoidPaymentRequestResponse>(
                `/portal/v1/payments/payment-requests/${paymentRequestUuid}/void`,
                VoidPaymentRequestResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        terminalSale: dedupe((data: TerminalSaleRequest) =>
            requestWithSchema<TerminalSaleResponse>(
                '/portal/v1/payments/terminal/sale',
                TerminalSaleResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        terminalTokenize: dedupe((data: TerminalTokenizationRequest) =>
            requestWithSchema<TerminalTokenizationResponse>(
                '/portal/v1/payments/terminal/tokenize',
                TerminalTokenizationResponseSchema,
                {
                    method: 'POST',
                    body: TerminalTokenizationRequestSchema.parse(data),
                }
            )
        ),

        terminalAbort: dedupe((data: TerminalAbortRequest) =>
            requestWithSchema<TerminalAbortResponse>(
                '/portal/v1/payments/terminal/abort',
                TerminalAbortResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        recordToken: dedupe((data: RecordTokenRequest) =>
            requestWithSchema<RecordTokenResponse>(
                '/portal/v1/payments/method/card-token',
                RecordTokenResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        processCardTokenPayment: dedupe((data: SubmitCardTokenPaymentRequest) =>
            requestWithSchema<ProcessCardTokenPaymentResponse>(
                '/portal/v1/payments/payment/card-token',
                ProcessCardTokenPaymentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        processCloverCardNotPresentPayment: dedupe(
            (data: ProcessCloverCardNotPresentPaymentRequest) =>
                requestWithSchema<ProcessCardNotPresentPaymentResponse>(
                    '/portal/v1/payments/payment/clover/card-not-present',
                    ProcessCardNotPresentPaymentResponseSchema,
                    {
                        method: 'POST',
                        body: ProcessCloverCardNotPresentPaymentRequestSchema.parse(data),
                    }
                )
        ),

        processStoredPaymentMethodPayment: dedupe(
            (data: ProcessStoredPaymentMethodPaymentRequest) => {
                const validatedData: ProcessStoredPaymentMethodPaymentRequest =
                    ProcessStoredPaymentMethodPaymentRequestSchema.parse(data);
                return requestWithSchema<ProcessCardTokenPaymentResponse>(
                    '/portal/v1/payments/payment/stored-method',
                    ProcessCardTokenPaymentResponseSchema,
                    {
                        method: 'POST',
                        body: validatedData,
                    }
                );
            }
        ),

        recordReconciliation: dedupe((data: RecordReconciliationRequest) =>
            requestWithSchema<RecordReconciliationResponse>(
                '/portal/v1/payments/reconcile',
                RecordReconciliationResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        recordPaymentRequestReconciliation: dedupe(
            (data: RecordPaymentRequestReconciliationRequest) =>
                requestWithSchema<RecordPaymentRequestReconciliationResponse>(
                    '/portal/v1/payments/payment-requests/reconcile',
                    RecordPaymentRequestReconciliationResponseSchema,
                    {
                        method: 'POST',
                        body: RecordPaymentRequestReconciliationRequestSchema.parse(data),
                    }
                )
        ),

        processAchPayment: dedupe((data: ProcessAchPaymentRequest) =>
            requestWithSchema<ProcessAchPaymentResponse>(
                '/portal/v1/payments/ach',
                ProcessAchPaymentResponseSchema,
                {
                    method: 'POST',
                    body: data,
                }
            )
        ),

        listCustomerPaymentMethods: (params: CustomerPaymentMethodListRequest = {}) => {
            const validatedParams: CustomerPaymentMethodListRequest =
                CustomerPaymentMethodListRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-methods?${querySuffix}`
                    : '/portal/v1/payments/customer/payment-methods';
            return requestWithSchema<CustomerPaymentMethodListResponse>(
                endpoint,
                CustomerPaymentMethodListResponseSchema
            );
        },

        listMerchantPaymentMethodTokens: (params: MerchantPaymentMethodTokenListRequest = {}) =>
            requestWithSchema<MerchantPaymentMethodTokenListResponse>(
                '/portal/v1/payments/payment-method-tokens/search',
                MerchantPaymentMethodTokenListResponseSchema,
                {
                    method: 'POST',
                    body: MerchantPaymentMethodTokenListRequestSchema.parse(params),
                }
            ),

        getMerchantPaymentMethodTokenDetails: (paymentMethodTokenUuid: string) =>
            requestWithSchema<MerchantPaymentMethodTokenDetailsView>(
                `/portal/v1/payments/payment-method-tokens/${paymentMethodTokenUuid}`,
                MerchantPaymentMethodTokenDetailsViewSchema
            ),

        getCustomerPaymentMethodBootstrap: (params: CustomerPaymentMethodBootstrapRequest = {}) => {
            const validatedParams: CustomerPaymentMethodBootstrapRequest =
                CustomerPaymentMethodBootstrapRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-methods/add/bootstrap?${querySuffix}`
                    : '/portal/v1/payments/customer/payment-methods/add/bootstrap';
            return requestWithSchema<CustomerPaymentMethodBootstrapResponse>(
                endpoint,
                CustomerPaymentMethodBootstrapResponseSchema
            );
        },

        customerPaymentMethodBinLookup: (data: CustomerPaymentMethodBinLookupRequest) =>
            requestWithSchema<BinLookupResponse>(
                '/portal/v1/payments/customer/payment-methods/add/bin-lookup',
                BinLookupResponseSchema,
                {
                    method: 'POST',
                    body: CustomerPaymentMethodBinLookupRequestSchema.parse(data),
                }
            ),

        createCustomerPaymentMethod: (data: CreateCustomerPaymentMethodRequest) =>
            requestWithSchema<CreateCustomerPaymentMethodResponse>(
                '/portal/v1/payments/customer/payment-methods',
                CreateCustomerPaymentMethodResponseSchema,
                {
                    method: 'POST',
                    body: CreateCustomerPaymentMethodRequestSchema.parse(data),
                }
            ),

        updateCustomerPaymentMethod: dedupe((data: UpdateCustomerPaymentMethodRequest) =>
            requestWithSchema<UpdateCustomerPaymentMethodResponse>(
                `/portal/v1/payments/customer/payment-methods/${data.payment_method_token_uuid}`,
                UpdateCustomerPaymentMethodResponseSchema,
                {
                    method: 'PATCH',
                    body: UpdateCustomerPaymentMethodRequestSchema.parse(data),
                }
            )
        ),

        getCustomerPortalDashboard: (params: CustomerPortalDashboardRequest = {}) => {
            const validatedParams: CustomerPortalDashboardRequest =
                CustomerPortalDashboardRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/dashboard?${querySuffix}`
                    : '/portal/v1/payments/customer/dashboard';
            return requestWithSchema<CustomerPortalDashboardResponse>(
                endpoint,
                CustomerPortalDashboardResponseSchema
            );
        },

        listCustomerPaymentRequests: (params: CustomerPaymentRequestSearchRequest = {}) => {
            const requestBody: CustomerPaymentRequestSearchRequest =
                CustomerPaymentRequestSearchRequestSchema.parse(params);
            return requestWithSchema<CustomerPaymentRequestListResponse>(
                '/portal/v1/payments/customer/payment-requests/search',
                CustomerPaymentRequestListResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getCustomerPaymentRequestDetails: (
            paymentRequestUuid: string,
            params: CustomerPaymentRequestDetailsRequest = {}
        ) => {
            const validatedParams: CustomerPaymentRequestDetailsRequest =
                CustomerPaymentRequestDetailsRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-requests/${paymentRequestUuid}?${querySuffix}`
                    : `/portal/v1/payments/customer/payment-requests/${paymentRequestUuid}`;
            return requestWithSchema<PaymentRequestDetailsView>(
                endpoint,
                PaymentRequestDetailsViewSchema
            );
        },

        listCustomerPaymentHistory: (params: CustomerPaymentHistoryRequest = {}) => {
            const validatedParams: CustomerPaymentHistoryRequest =
                CustomerPaymentHistoryRequestSchema.parse(params);
            const queryParams: URLSearchParams = new URLSearchParams();
            if (validatedParams.merchant_organization_uuid !== undefined) {
                queryParams.set(
                    'merchant_organization_uuid',
                    validatedParams.merchant_organization_uuid
                );
            }
            if (validatedParams.customer_organization_uuid !== undefined) {
                queryParams.set(
                    'customer_organization_uuid',
                    validatedParams.customer_organization_uuid
                );
            }
            if (validatedParams.limit !== undefined) {
                queryParams.set('limit', String(validatedParams.limit));
            }
            if (validatedParams.cursor !== undefined) {
                queryParams.set('cursor', String(validatedParams.cursor));
            }
            const querySuffix: string = queryParams.toString();
            const endpoint: string =
                querySuffix.length > 0
                    ? `/portal/v1/payments/customer/payment-history?${querySuffix}`
                    : '/portal/v1/payments/customer/payment-history';
            return requestWithSchema<CustomerPaymentHistoryResponse>(
                endpoint,
                CustomerPaymentHistoryResponseSchema
            );
        },
    };

    const sambaApi = {
        getVersion: (headers?: HttpRequestHeaders) =>
            requestWithSchema<SambaVersionResponse>('/samba/v1/version', SambaVersionResponseSchema, {
                headers,
            }),
        listModules: (headers?: HttpRequestHeaders) =>
            requestWithSchema<SambaModuleListResponse>(
                '/samba/v1/modules',
                SambaModuleListResponseSchema,
                {
                    headers,
                }
            ),
        getModuleSource: (
            moduleName: RpaModuleName,
            version: string,
            headers?: HttpRequestHeaders
        ) =>
            requestTextWithSchema(
                `/samba/v1/modules/${z.string().min(1).parse(moduleName)}.${z
                    .string()
                    .min(1)
                    .parse(version)}.js`,
                z.string().min(1),
                {
                    headers,
                }
            ),
        getLatestRelease: (params: SambaLatestReleaseQuery, headers?: HttpRequestHeaders) => {
            const queryParams: URLSearchParams = new URLSearchParams({
                platform: params.platform,
                arch: params.arch,
            });
            return requestWithSchema<SambaLatestReleaseResponse>(
                `/samba/latest?${queryParams.toString()}`,
                SambaLatestReleaseResponseSchema,
                {
                    headers,
                }
            );
        },
        getInstallerQa: (params: SambaInstallerQaQuery, headers?: HttpRequestHeaders) => {
            const query = SambaInstallerQaQuerySchema.parse(params);
            const queryParams = new URLSearchParams({
                track: query.track,
                arch: query.arch,
            });
            return requestWithSchema<WindowsArtifactQa>(
                `/samba/installer-qa?${queryParams.toString()}`,
                WindowsArtifactQaSchema,
                { headers }
            );
        },
    };

    const diagnosticsApi = {
        getEnvironmentVariables: () =>
            requestWithSchema<Record<string, string>>(
                '/portal/v1/diagnostics/superadmin/env',
                EnvironmentVariablesResponseSchema
            ),

        getAwsCredentials: () =>
            requestWithSchema<AwsCredentialsResponse>(
                '/portal/v1/diagnostics/superadmin/aws-credentials',
                AwsCredentialsResponseSchema
            ),

        getCallerIdentity: () =>
            requestWithSchema<GetCallerIdentityResponse>(
                '/portal/v1/diagnostics/superadmin/aws-caller-identity',
                GetCallerIdentityResponseSchema
            ),

        getSystemInfo: () =>
            requestWithSchema<GetSystemInfoResponse>(
                '/portal/v1/diagnostics/superadmin/system-info',
                GetSystemInfoResponseSchema
            ),

        getSesDiagnostics: () =>
            requestWithSchema<GetSesDiagnosticsResponse>(
                '/portal/v1/diagnostics/superadmin/ses',
                GetSesDiagnosticsResponseSchema
            ),
    };

    const encryptionKeysApi = {
        listKeys: (query?: ListEncryptionKeysRequest) => {
            const parsedQuery =
                query === undefined ? undefined : ListEncryptionKeysRequestSchema.parse(query);
            const searchParams: URLSearchParams = new URLSearchParams();
            if (parsedQuery?.limit !== undefined) {
                searchParams.set('limit', String(parsedQuery.limit));
            }
            if (parsedQuery?.cursor !== undefined) {
                searchParams.set('cursor', parsedQuery.cursor);
            }
            const serializedQuery: string = searchParams.toString();
            const endpoint: string =
                serializedQuery.length > 0
                    ? `/portal/v1/encryption_keys?${serializedQuery}`
                    : '/portal/v1/encryption_keys';
            return requestWithSchema<ListEncryptionKeysResponse>(
                endpoint,
                ListEncryptionKeysResponseSchema
            );
        },

        rotateCurrentKey: dedupe(() =>
            requestWithSchema<RotateEncryptionKeyResponse>(
                '/portal/v1/encryption_keys/rotate',
                RotateEncryptionKeyResponseSchema,
                {
                    method: 'POST',
                }
            )
        ),

        rekeyStaleUsage: dedupe((request?: RekeyEncryptionKeyUsageRequest) =>
            requestWithSchema<RekeyEncryptionKeyUsageResponse>(
                '/portal/v1/encryption_keys/rekey_stale_usage',
                RekeyEncryptionKeyUsageResponseSchema,
                {
                    method: 'POST',
                    body:
                        request === undefined
                            ? {}
                            : RekeyEncryptionKeyUsageRequestSchema.parse(request),
                }
            )
        ),

        listCredentials: (secretName: string, query?: ListEncryptionKeyCredentialsRequest) => {
            const parsedQuery =
                query === undefined
                    ? undefined
                    : ListEncryptionKeyCredentialsRequestSchema.parse(query);
            const searchParams: URLSearchParams = new URLSearchParams();
            if (parsedQuery?.limit !== undefined) {
                searchParams.set('limit', String(parsedQuery.limit));
            }
            if (parsedQuery?.cursor !== undefined) {
                searchParams.set('cursor', parsedQuery.cursor);
            }
            const serializedQuery: string = searchParams.toString();
            const endpointBase = `/portal/v1/encryption_keys/${encodeURIComponent(
                secretName
            )}/credentials`;
            const endpoint =
                serializedQuery.length > 0 ? `${endpointBase}?${serializedQuery}` : endpointBase;
            return requestWithSchema<ListEncryptionKeyCredentialsResponse>(
                endpoint,
                ListEncryptionKeyCredentialsResponseSchema
            );
        },

        listCredentialRotationAttempts: (
            secretName: string,
            query?: ListEncryptionKeyCredentialRotationAttemptsRequest
        ) => {
            const parsedQuery =
                query === undefined
                    ? undefined
                    : ListEncryptionKeyCredentialRotationAttemptsRequestSchema.parse(query);
            const searchParams: URLSearchParams = new URLSearchParams();
            if (parsedQuery?.limit !== undefined) {
                searchParams.set('limit', String(parsedQuery.limit));
            }
            if (parsedQuery?.cursor !== undefined) {
                searchParams.set('cursor', parsedQuery.cursor);
            }
            const serializedQuery: string = searchParams.toString();
            const endpointBase = `/portal/v1/encryption_keys/${encodeURIComponent(
                secretName
            )}/credential_rotation_attempts`;
            const endpoint =
                serializedQuery.length > 0 ? `${endpointBase}?${serializedQuery}` : endpointBase;
            return requestWithSchema<ListEncryptionKeyCredentialRotationAttemptsResponse>(
                endpoint,
                ListEncryptionKeyCredentialRotationAttemptsResponseSchema
            );
        },
    };

    const secretsApi = {
        listSecrets: (
            organization: SecretOrganizationReference | SecretOrganizationUuidReference,
            query?: ListSecretsForOrganizationQuery
        ) => {
            const parsedQuery: ListSecretsForOrganizationQuery | undefined =
                query !== undefined
                    ? ListSecretsForOrganizationQuerySchema.parse(query)
                    : undefined;
            const searchParams: URLSearchParams = new URLSearchParams();
            if (parsedQuery?.actions !== undefined) {
                for (const action of parsedQuery.actions) {
                    searchParams.append('actions', action);
                }
            }
            if (parsedQuery?.credential_type !== undefined) {
                searchParams.set('credential_type', parsedQuery.credential_type);
            }
            if (parsedQuery?.terms !== undefined) {
                for (const term of parsedQuery.terms) {
                    searchParams.append('terms', term);
                }
            }
            if (parsedQuery?.include_descendants !== undefined) {
                searchParams.set(
                    'include_descendants',
                    parsedQuery.include_descendants ? 'true' : 'false'
                );
            }
            if (parsedQuery?.show_deleted === true) {
                searchParams.set('show_deleted', 'true');
            }
            if (parsedQuery?.system_ids !== undefined) {
                for (const systemId of parsedQuery.system_ids) {
                    searchParams.append('system_ids', String(systemId));
                }
            }
            if (parsedQuery?.exclude_policy_linked === true) {
                searchParams.set('exclude_policy_linked', 'true');
            }
            if (parsedQuery?.limit !== undefined) {
                searchParams.set('limit', String(parsedQuery.limit));
            }
            if (parsedQuery?.cursor !== undefined) {
                searchParams.set('cursor', parsedQuery.cursor);
            }
            const organizationReferenceAttempt =
                SecretOrganizationReferenceSchema.safeParse(organization);
            const organizationUuidReferenceAttempt =
                SecretOrganizationUuidReferenceSchema.safeParse(organization);
            const organizationUuid: string = organizationReferenceAttempt.success
                ? organizationReferenceAttempt.data.organization_uuid
                : organizationUuidReferenceAttempt.success
                  ? organizationUuidReferenceAttempt.data.organization_uuid
                  : z.string().uuid().parse(organization);
            const serializedQuery: string = searchParams.toString();
            const endpoint: string =
                serializedQuery.length > 0
                    ? `/portal/v1/secrets/organization/${organizationUuid}?${serializedQuery}`
                    : `/portal/v1/secrets/organization/${organizationUuid}`;
            return requestWithSchema<ListSecretsResponse>(endpoint, ListSecretsResponseSchema);
        },

        getSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            requestWithSchema<GetSecretResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}`,
                GetSecretResponseSchema
            ),

        getSecretWithCredentials: (organizationUuid: string, systemId: number, accountId: string) =>
            requestWithSchema<SecretWithCredentialsResponse>(
                `/portal/v1/secrets/${organizationUuid}/${systemId}/${accountId}/credentials`,
                SecretWithCredentialsResponseSchema
            ),

        getSecretForUse: (organizationUuid: string, systemId: number, accountId: string) =>
            requestWithSchema<SecretWithCredentialsResponse>(
                `/portal/v1/secrets/${organizationUuid}/${systemId}/${accountId}/use-credentials`,
                SecretWithCredentialsResponseSchema
            ),

        listCredentialDefinitions: (query: ListSecretCredentialDefinitionsQuery) => {
            const parsedQuery = ListSecretCredentialDefinitionsQuerySchema.parse(query);
            const searchParams: URLSearchParams = new URLSearchParams();
            searchParams.set('organization_uuid', parsedQuery.organization_uuid);
            for (const systemId of parsedQuery.system_ids ?? []) {
                searchParams.append('system_ids', String(systemId));
            }
            for (const credentialType of parsedQuery.credential_types ?? []) {
                searchParams.append('credential_types', credentialType);
            }
            return requestWithSchema<ListSecretCredentialDefinitionsResponse>(
                `/portal/v1/secrets/credential-definitions?${searchParams.toString()}`,
                ListSecretCredentialDefinitionsResponseSchema
            );
        },

        createSecret: dedupe((data: CreateSecretRequest) =>
            requestWithSchema<CreateSecretResponse>('/portal/v1/secrets', CreateSecretResponseSchema, {
                method: 'POST',
                body: CreateSecretRequestSchema.parse(data),
            })
        ),

        updateSecretByAccount: dedupe(
            (
                organizationUuid: string,
                systemId: number,
                accountId: string,
                data: UpdateSecretRequest
            ) =>
                requestWithSchema<SecretMetadataResponse>(
                    `/portal/v1/secrets/${organizationUuid}/${systemId}/${accountId}`,
                    SecretMetadataResponseSchema,
                    {
                        method: 'PUT',
                        body: data,
                    }
                )
        ),

        updateSecret: dedupe(
            (secret: SecretIdentityReference | SecretUuidReference, data: UpdateSecretRequest) =>
                requestWithSchema<SecretMetadataResponse>(
                    `/portal/v1/secrets/${(() => {
                        const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                        return validatedSecret.credential_uuid;
                    })()}`,
                    SecretMetadataResponseSchema,
                    {
                        method: 'PATCH',
                        body: data,
                    }
                )
        ),

        disableSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}/disable`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        enableSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}/enable`,
                MessageResponseSchema,
                {
                    method: 'POST',
                }
            ),

        deleteSecret: (secret: SecretIdentityReference | SecretUuidReference) =>
            requestWithSchema<MessageResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}`,
                MessageResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        listCredentialUsages: (secret: SecretIdentityReference | SecretUuidReference) =>
            requestWithSchema<CredentialUsagesResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}/usages`,
                CredentialUsagesResponseSchema
            ),

        disconnectCredentialUsages: (
            secret: SecretIdentityReference | SecretUuidReference,
            data: DisconnectCredentialUsagesRequest
        ) =>
            requestWithSchema<DisconnectCredentialUsagesResponse>(
                `/portal/v1/secrets/${(() => {
                    const validatedSecret = SecretUuidReferenceSchema.parse(secret);
                    return validatedSecret.credential_uuid;
                })()}/disconnect`,
                DisconnectCredentialUsagesResponseSchema,
                {
                    method: 'POST',
                    body: DisconnectCredentialUsagesRequestSchema.parse(data),
                }
            ),
    };

    const rpaCredentialInjectionApi = {
        bootstrap: dedupe((data: IpxCredentialRuntimeBootstrapRequest) =>
            requestWithSchema<IpxCredentialRuntimeBootstrapResponse>(
                '/portal/v1/rpa-credential-injection/bootstrap',
                IpxCredentialRuntimeBootstrapResponseSchema,
                {
                    method: 'POST',
                    body: IpxCredentialRuntimeBootstrapRequestSchema.parse(data),
                }
            )
        ),
        resolve: dedupe((data: IpxCredentialRuntimeResolveRequest) =>
            requestWithSchema<IpxCredentialRuntimeResolveResponse>(
                '/portal/v1/rpa-credential-injection/resolve',
                IpxCredentialRuntimeResolveResponseSchema,
                {
                    method: 'POST',
                    body: IpxCredentialRuntimeResolveRequestSchema.parse(data),
                }
            )
        ),
        rotationBegin: dedupe((data: CredentialRotationBeginRequest) =>
            requestWithSchema<CredentialRotationBeginResponse>(
                '/portal/v1/rpa-credential-injection/rotation/begin',
                CredentialRotationBeginResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationBeginRequestSchema.parse(data),
                }
            )
        ),
        rotationInjected: dedupe((data: CredentialRotationInjectedRequest) =>
            requestWithSchema<void>(
                '/portal/v1/rpa-credential-injection/rotation/injected',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationInjectedRequestSchema.parse(data),
                    acceptedStatusCodes: [204],
                }
            )
        ),
        rotationSubmitted: dedupe((data: CredentialRotationSubmittedRequest) =>
            requestWithSchema<void>(
                '/portal/v1/rpa-credential-injection/rotation/submitted',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationSubmittedRequestSchema.parse(data),
                    acceptedStatusCodes: [204],
                }
            )
        ),
        rotationPromote: dedupe((data: CredentialRotationPromoteRequest) =>
            requestWithSchema<CredentialRotationPromoteResponse>(
                '/portal/v1/rpa-credential-injection/rotation/promote',
                CredentialRotationPromoteResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationPromoteRequestSchema.parse(data),
                }
            )
        ),
        rotationFail: dedupe((data: CredentialRotationFailRequest) =>
            requestWithSchema<void>(
                '/portal/v1/rpa-credential-injection/rotation/fail',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: CredentialRotationFailRequestSchema.parse(data),
                    acceptedStatusCodes: [204],
                }
            )
        ),
        manualCapture: dedupe((data: ManualCredentialCaptureRequest) =>
            requestWithSchema<ManualCredentialCaptureResponse>(
                '/portal/v1/rpa-credential-injection/manual-capture',
                ManualCredentialCaptureResponseSchema,
                {
                    method: 'POST',
                    body: ManualCredentialCaptureRequestSchema.parse(data),
                }
            )
        ),
    };

    const rbsIpsWorkflowRuntimeApi = {
        bootstrap: dedupe((data: IpxRbsWorkflowRuntimeBootstrapRequest) =>
            requestWithSchema<IpxRbsWorkflowRuntimeBootstrapResponse>(
                '/portal/v1/rbs-ips-workflows/runtime/bootstrap',
                IpxRbsWorkflowRuntimeBootstrapResponseSchema,
                {
                    method: 'POST',
                    body: IpxRbsWorkflowRuntimeBootstrapRequestSchema.parse(data),
                }
            )
        ),
    };

    const ipxCredentialManagementApi = {
        searchCredentialDefinitions: dedupe((data: IpxCredentialDefinitionSearchRequest) =>
            requestWithSchema<IpxCredentialDefinitionSearchResponse>(
                '/portal/v1/ipx-credential-management/credential-definitions/search',
                IpxCredentialDefinitionSearchResponseSchema,
                {
                    method: 'POST',
                    body: IpxCredentialDefinitionSearchRequestSchema.parse(data),
                }
            )
        ),

        searchOrganizationCredentialPolicyDefinitions: dedupe(
            (data: SearchOrganizationIpxCredentialPolicyDefinitionsRequest) => {
                const requestBody: SearchOrganizationIpxCredentialPolicyDefinitionsRequest =
                    SearchOrganizationIpxCredentialPolicyDefinitionsRequestSchema.parse(data);
                return requestWithSchema<SearchOrganizationIpxCredentialPolicyDefinitionsResponse>(
                    `/portal/v1/ipx-credential-management/organizations/${requestBody.organization_uuid}/policy-credential-definitions/search`,
                    SearchOrganizationIpxCredentialPolicyDefinitionsResponseSchema,
                    {
                        method: 'POST',
                        body: IpxCredentialDefinitionSearchRequestSchema.parse({
                            terms: requestBody.terms,
                            limit: requestBody.limit,
                            cursor: requestBody.cursor,
                            status: requestBody.status,
                            ipx_system_id: requestBody.ipx_system_id,
                        }),
                    }
                );
            }
        ),

        listOrganizationCredentialPolicies: (data: ListOrganizationIpxCredentialPoliciesRequest) =>
            requestWithSchema<ListOrganizationIpxCredentialPoliciesResponse>(
                buildIpxCredentialManagementOrganizationPoliciesEndpoint(data),
                ListOrganizationIpxCredentialPoliciesResponseSchema
            ),

        listDashboardWebsiteCredentials: (data: ListDashboardWebsiteCredentialsRequest) =>
            requestWithSchema<ListDashboardWebsiteCredentialsResponse>(
                buildIpxCredentialManagementDashboardWebsiteCredentialsEndpoint(data),
                ListDashboardWebsiteCredentialsResponseSchema
            ),

        createDashboardWebsiteCredential: dedupe(
            (data: CreateDashboardWebsiteCredentialRequest) => {
                const requestBody: CreateDashboardWebsiteCredentialRequest =
                    CreateDashboardWebsiteCredentialRequestSchema.parse(data);
                return requestWithSchema<IpxCredentialSummaryListItem>(
                    buildIpxCredentialManagementDashboardWebsiteCredentialEndpoint({
                        organization_uuid: requestBody.organization_uuid,
                        organization_ipx_credential_policy_uuid:
                            requestBody.organization_ipx_credential_policy_uuid,
                    }),
                    IpxCredentialSummaryListItemSchema,
                    {
                        method: 'POST',
                        body: {
                            account_id: requestBody.account_id,
                            edited_fields: requestBody.edited_fields,
                        },
                    }
                );
            }
        ),

        createOrganizationCredentialPolicy: dedupe(
            (data: CreateOrganizationIpxCredentialPolicyRequest) => {
                const requestBody: CreateOrganizationIpxCredentialPolicyRequest =
                    CreateOrganizationIpxCredentialPolicyRequestSchema.parse(data);
                return requestWithSchema<OrganizationIpxCredentialPolicyResponse>(
                    buildIpxCredentialManagementOrganizationPoliciesEndpoint({
                        organization_uuid: requestBody.organization_uuid,
                    }),
                    OrganizationIpxCredentialPolicyResponseSchema,
                    {
                        method: 'POST',
                        body: {
                            ipx_credential_definition_uuid:
                                requestBody.ipx_credential_definition_uuid,
                        },
                    }
                );
            }
        ),

        disableOrganizationCredentialPolicy: dedupe(
            (data: OrganizationIpxCredentialPolicyLifecycleRequest) => {
                const requestBody: OrganizationIpxCredentialPolicyLifecycleRequest =
                    OrganizationIpxCredentialPolicyLifecycleRequestSchema.parse(data);
                return requestWithSchema<OrganizationIpxCredentialPolicyResponse>(
                    buildIpxCredentialManagementPolicyEndpoint(
                        requestBody.organization_ipx_credential_policy_uuid,
                        '/disable'
                    ),
                    OrganizationIpxCredentialPolicyResponseSchema,
                    {
                        method: 'POST',
                    }
                );
            }
        ),

        enableOrganizationCredentialPolicy: dedupe(
            (data: OrganizationIpxCredentialPolicyLifecycleRequest) => {
                const requestBody: OrganizationIpxCredentialPolicyLifecycleRequest =
                    OrganizationIpxCredentialPolicyLifecycleRequestSchema.parse(data);
                return requestWithSchema<OrganizationIpxCredentialPolicyResponse>(
                    buildIpxCredentialManagementPolicyEndpoint(
                        requestBody.organization_ipx_credential_policy_uuid,
                        '/enable'
                    ),
                    OrganizationIpxCredentialPolicyResponseSchema,
                    {
                        method: 'POST',
                    }
                );
            }
        ),

        deleteOrganizationCredentialPolicy: dedupe(
            (data: OrganizationIpxCredentialPolicyLifecycleRequest) => {
                const requestBody: OrganizationIpxCredentialPolicyLifecycleRequest =
                    OrganizationIpxCredentialPolicyLifecycleRequestSchema.parse(data);
                return requestWithSchema<OrganizationIpxCredentialPolicyResponse>(
                    buildIpxCredentialManagementPolicyEndpoint(
                        requestBody.organization_ipx_credential_policy_uuid
                    ),
                    OrganizationIpxCredentialPolicyResponseSchema,
                    {
                        method: 'DELETE',
                    }
                );
            }
        ),

        restoreOrganizationCredentialPolicy: dedupe(
            (data: OrganizationIpxCredentialPolicyLifecycleRequest) => {
                const requestBody: OrganizationIpxCredentialPolicyLifecycleRequest =
                    OrganizationIpxCredentialPolicyLifecycleRequestSchema.parse(data);
                return requestWithSchema<OrganizationIpxCredentialPolicyResponse>(
                    buildIpxCredentialManagementPolicyEndpoint(
                        requestBody.organization_ipx_credential_policy_uuid,
                        '/restore'
                    ),
                    OrganizationIpxCredentialPolicyResponseSchema,
                    {
                        method: 'POST',
                    }
                );
            }
        ),

        listCredentialsForPolicy: (policyUuid: string) =>
            requestWithSchema<ListIpxCredentialsForPolicyResponse>(
                buildIpxCredentialManagementPolicyEndpoint(policyUuid, '/credentials'),
                ListIpxCredentialsForPolicyResponseSchema
            ),

        createCredentialForPolicy: dedupe((policyUuid: string, data: CreateIpxCredentialRequest) =>
            requestWithSchema<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementPolicyEndpoint(policyUuid, '/credentials'),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'POST',
                    body: CreateIpxCredentialRequestSchema.parse(data),
                }
            )
        ),

        updatePolicyCredential: dedupe((data: UpdateIpxCredentialRequest) => {
            const requestBody: UpdateIpxCredentialRequest =
                UpdateIpxCredentialRequestSchema.parse(data);
            return requestWithSchema<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementCredentialEndpoint(requestBody.credential_uuid),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'PATCH',
                    body: {
                        account_id: requestBody.account_id,
                        edited_fields: requestBody.edited_fields,
                    },
                }
            );
        }),

        revealPolicyCredentialSecretField: dedupe((data: RevealIpxCredentialSecretFieldRequest) => {
            const requestBody: RevealIpxCredentialSecretFieldRequest =
                RevealIpxCredentialSecretFieldRequestSchema.parse(data);
            return requestWithSchema<RevealIpxCredentialSecretFieldResponse>(
                buildIpxCredentialManagementCredentialEndpoint(
                    requestBody.credential_uuid,
                    '/reveal-field'
                ),
                RevealIpxCredentialSecretFieldResponseSchema,
                {
                    method: 'POST',
                    body: {
                        field_name: requestBody.field_name,
                    },
                }
            );
        }),

        disablePolicyCredential: dedupe((credentialUuid: string) =>
            requestWithSchema<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementCredentialEndpoint(credentialUuid, '/disable'),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'POST',
                }
            )
        ),

        enablePolicyCredential: dedupe((credentialUuid: string) =>
            requestWithSchema<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementCredentialEndpoint(credentialUuid, '/enable'),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'POST',
                }
            )
        ),

        deletePolicyCredential: dedupe((credentialUuid: string) =>
            requestWithSchema<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementCredentialEndpoint(credentialUuid),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'DELETE',
                }
            )
        ),

        restorePolicyCredential: dedupe((credentialUuid: string) =>
            requestWithSchema<IpxCredentialSummaryListItem>(
                buildIpxCredentialManagementCredentialEndpoint(credentialUuid, '/restore'),
                IpxCredentialSummaryListItemSchema,
                {
                    method: 'POST',
                }
            )
        ),

        listCredentialRotationHistory: (data: CredentialRotationHistoryRequest) =>
            requestWithSchema<CredentialRotationHistoryResponse>(
                buildIpxCredentialManagementCredentialRotationHistoryEndpoint(data),
                CredentialRotationHistoryResponseSchema
            ),

        manualPromoteCredentialRotationAttempt: dedupe(
            (data: CredentialRotationManualPromotionRequest) => {
                const requestBody: CredentialRotationManualPromotionRequest =
                    CredentialRotationManualPromotionRequestSchema.parse(data);
                return requestWithSchema<CredentialRotationManualPromotionResponse>(
                    `/portal/v1/ipx-credential-management/rotation-attempts/${encodeURIComponent(
                        requestBody.credential_rotation_attempt_uuid
                    )}/manual-promote`,
                    CredentialRotationManualPromotionResponseSchema,
                    {
                        method: 'POST',
                        body: {
                            confirmation: requestBody.confirmation,
                        },
                    }
                );
            }
        ),
    };

    const rpaBuilderApi = {
        bootstrap: dedupe((data: RpaBuilderBootstrapRequest) =>
            requestWithSchema<RpaBuilderBootstrapResponse>(
                '/portal/v1/rpa-builder/bootstrap',
                RpaBuilderBootstrapResponseSchema,
                {
                    method: 'POST',
                    body: RpaBuilderBootstrapRequestSchema.parse(data),
                }
            )
        ),

        searchAllowedWebsiteTemplates: (params: AllowedWebsiteTemplateSearchRequest = {}) => {
            const requestBody: AllowedWebsiteTemplateSearchRequest =
                AllowedWebsiteTemplateSearchRequestSchema.parse(params);
            return requestWithSchema<AllowedWebsiteTemplateSearchPageResponse>(
                '/portal/v1/rpa-builder/allowed-website-templates/search',
                AllowedWebsiteTemplateSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getAllowedWebsiteTemplate: (templateUuid: string) =>
            requestWithSchema<AllowedWebsiteTemplateMutationResponse>(
                `/portal/v1/rpa-builder/allowed-website-templates/${encodeURIComponent(
                    AllowedWebsiteTemplateUuidRequestSchema.parse({
                        allowed_website_template_uuid: templateUuid,
                    }).allowed_website_template_uuid
                )}`,
                AllowedWebsiteTemplateMutationResponseSchema
            ),

        createAllowedWebsiteTemplate: dedupe((data: CreateAllowedWebsiteTemplateRequest) => {
            const requestBody: CreateAllowedWebsiteTemplateRequest =
                CreateAllowedWebsiteTemplateRequestSchema.parse(data);
            return requestWithSchema<AllowedWebsiteTemplateMutationResponse>(
                '/portal/v1/rpa-builder/allowed-website-templates',
                AllowedWebsiteTemplateMutationResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        }),

        updateAllowedWebsiteTemplate: dedupe(
            (templateUuid: string, data: UpdateAllowedWebsiteTemplateRequest) => {
                const requestData: AllowedWebsiteTemplateUuidRequest =
                    AllowedWebsiteTemplateUuidRequestSchema.parse({
                        allowed_website_template_uuid: templateUuid,
                    });
                const requestBody: UpdateAllowedWebsiteTemplateRequest =
                    UpdateAllowedWebsiteTemplateRequestSchema.parse(data);
                return requestWithSchema<AllowedWebsiteTemplateMutationResponse>(
                    `/portal/v1/rpa-builder/allowed-website-templates/${encodeURIComponent(
                        requestData.allowed_website_template_uuid
                    )}`,
                    AllowedWebsiteTemplateMutationResponseSchema,
                    {
                        method: 'PATCH',
                        body: requestBody,
                    }
                );
            }
        ),

        listCredentialDefinitions: (data: RpaBuilderListCredentialDefinitionsRequest) =>
            requestWithSchema<RpaBuilderListCredentialDefinitionsResponse>(
                buildRpaBuilderListCredentialDefinitionsEndpoint(data),
                RpaBuilderListCredentialDefinitionsResponseSchema
            ),

        createCredentialDefinition: dedupe((data: RpaBuilderCreateCredentialDefinitionRequest) =>
            requestWithSchema<RpaBuilderSaveCredentialDefinitionResponse>(
                '/portal/v1/rpa-builder/credential-definitions',
                RpaBuilderSaveCredentialDefinitionResponseSchema,
                {
                    method: 'POST',
                    body: RpaBuilderCreateCredentialDefinitionRequestSchema.parse(data),
                }
            )
        ),

        updateCredentialDefinition: dedupe((data: RpaBuilderUpdateCredentialDefinitionRequest) => {
            const requestBody: RpaBuilderUpdateCredentialDefinitionRequest =
                RpaBuilderUpdateCredentialDefinitionRequestSchema.parse(data);
            return requestWithSchema<RpaBuilderSaveCredentialDefinitionResponse>(
                buildRpaBuilderDefinitionEndpoint(requestBody.ipx_credential_definition_uuid),
                RpaBuilderSaveCredentialDefinitionResponseSchema,
                {
                    method: 'PUT',
                    body: requestBody,
                }
            );
        }),

        saveWebsiteCredentialConfiguration: dedupe(
            (data: RpaBuilderSaveWebsiteCredentialConfigurationRequest) =>
                requestWithSchema<RpaBuilderSaveWebsiteCredentialConfigurationResponse>(
                    '/portal/v1/rpa-builder/website-configuration/credentials',
                    RpaBuilderSaveWebsiteCredentialConfigurationResponseSchema,
                    {
                        method: 'PUT',
                        body: RpaBuilderSaveWebsiteCredentialConfigurationRequestSchema.parse(data),
                    }
                )
        ),

        deleteCredentialDefinition: dedupe((data: RpaBuilderDeleteCredentialDefinitionRequest) => {
            const requestData: RpaBuilderDeleteCredentialDefinitionRequest =
                RpaBuilderDeleteCredentialDefinitionRequestSchema.parse(data);
            return requestWithSchema<RpaBuilderDeleteCredentialDefinitionResponse>(
                buildRpaBuilderDefinitionEndpoint(requestData.ipx_credential_definition_uuid),
                RpaBuilderDeleteCredentialDefinitionResponseSchema,
                {
                    method: 'DELETE',
                }
            );
        }),

        resetInvalidLoginWorkflow: dedupe((data: RpaBuilderResetInvalidLoginWorkflowRequest) => {
            const requestData: RpaBuilderResetInvalidLoginWorkflowRequest =
                RpaBuilderResetInvalidLoginWorkflowRequestSchema.parse(data);
            return requestWithSchema<RpaBuilderResetInvalidLoginWorkflowResponse>(
                buildRpaBuilderDefinitionEndpoint(
                    requestData.ipx_credential_definition_uuid,
                    '/reset-invalid-login-workflow'
                ),
                RpaBuilderResetInvalidLoginWorkflowResponseSchema,
                {
                    method: 'POST',
                }
            );
        }),

        attachCredentialPolicy: dedupe((data: RpaBuilderAttachCredentialPolicyRequest) => {
            const requestBody: RpaBuilderAttachCredentialPolicyRequest =
                RpaBuilderAttachCredentialPolicyRequestSchema.parse(data);
            return requestWithSchema<RpaBuilderAttachCredentialPolicyResponse>(
                buildRpaBuilderDefinitionEndpoint(
                    requestBody.ipx_credential_definition_uuid,
                    '/policy'
                ),
                RpaBuilderAttachCredentialPolicyResponseSchema,
                {
                    method: 'PUT',
                    body: requestBody,
                }
            );
        }),

        reactivateCredentialPolicy: dedupe((data: RpaBuilderReactivateCredentialPolicyRequest) => {
            const requestBody: RpaBuilderReactivateCredentialPolicyRequest =
                RpaBuilderReactivateCredentialPolicyRequestSchema.parse(data);
            return requestWithSchema<RpaBuilderReactivateCredentialPolicyResponse>(
                buildRpaBuilderDefinitionEndpoint(
                    requestBody.ipx_credential_definition_uuid,
                    '/policy'
                ),
                RpaBuilderReactivateCredentialPolicyResponseSchema,
                {
                    method: 'PUT',
                    body: requestBody,
                }
            );
        }),

        deactivateCredentialPolicy: dedupe((data: RpaBuilderDeactivateCredentialPolicyRequest) => {
            const requestBody: RpaBuilderDeactivateCredentialPolicyRequest =
                RpaBuilderDeactivateCredentialPolicyRequestSchema.parse(data);
            return requestWithSchema<RpaBuilderDeactivateCredentialPolicyResponse>(
                buildRpaBuilderDefinitionEndpoint(
                    requestBody.ipx_credential_definition_uuid,
                    '/policy/deactivate'
                ),
                RpaBuilderDeactivateCredentialPolicyResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        }),

        listWebResourceModels: (data: RpaBuilderListWebResourceModelsRequest = {}) =>
            requestWithSchema<RpaBuilderListWebResourceModelsResponse>(
                buildRpaBuilderListWebResourceModelsEndpoint(data),
                RpaBuilderListWebResourceModelsResponseSchema
            ),

        fetchWebResourceModel: (data: RpaBuilderFetchWebResourceModelRequest) => {
            const requestData: RpaBuilderFetchWebResourceModelRequest =
                RpaBuilderFetchWebResourceModelRequestSchema.parse(data);
            return requestWithSchema<RpaBuilderFetchWebResourceModelResponse>(
                buildRpaBuilderWebResourceModelEndpoint(requestData.ipx_web_resource_model_uuid),
                RpaBuilderFetchWebResourceModelResponseSchema
            );
        },

        createWebResourceModel: dedupe((data: RpaBuilderCreateWebResourceModelRequest) =>
            requestWithSchema<RpaBuilderSaveWebResourceModelResponse>(
                '/portal/v1/rpa-builder/web-resource-models',
                RpaBuilderSaveWebResourceModelResponseSchema,
                {
                    method: 'POST',
                    body: RpaBuilderCreateWebResourceModelRequestSchema.parse(data),
                }
            )
        ),

        replaceWebResourceModel: dedupe((data: RpaBuilderReplaceWebResourceModelRequest) => {
            const requestBody: RpaBuilderReplaceWebResourceModelRequest =
                RpaBuilderReplaceWebResourceModelRequestSchema.parse(data);
            return requestWithSchema<RpaBuilderSaveWebResourceModelResponse>(
                buildRpaBuilderWebResourceModelEndpoint(requestBody.ipx_web_resource_model_uuid),
                RpaBuilderSaveWebResourceModelResponseSchema,
                {
                    method: 'PUT',
                    body: requestBody,
                }
            );
        }),

        deleteWebResourceModel: dedupe((data: RpaBuilderDeleteWebResourceModelRequest) => {
            const requestData: RpaBuilderDeleteWebResourceModelRequest =
                RpaBuilderDeleteWebResourceModelRequestSchema.parse(data);
            return requestWithSchema<RpaBuilderDeleteWebResourceModelResponse>(
                buildRpaBuilderWebResourceModelEndpoint(requestData.ipx_web_resource_model_uuid),
                RpaBuilderDeleteWebResourceModelResponseSchema,
                {
                    method: 'DELETE',
                }
            );
        }),
    };

    const permissionsApi = {
        getRoleAssignmentPermissions: (roleAssignmentUuid: string) =>
            requestWithSchema<RoleAssignmentPermissionsView>(
                `/portal/v1/permissions/role-assignment/${roleAssignmentUuid}`,
                RoleAssignmentPermissionsViewSchema
            ),

        auditRoleAssignmentPermissions: (roleAssignmentUuid: string) =>
            requestWithSchema<RoleAssignmentPermissionAuditResponse>(
                `/portal/v1/permissions/audit/role-assignment/${roleAssignmentUuid}`,
                RoleAssignmentPermissionAuditResponseSchema
            ),

        auditOrganizationPermissions: (organizationUuid: string) =>
            requestWithSchema<OrganizationPermissionAuditResponse>(
                `/portal/v1/permissions/audit/organization/${organizationUuid}`,
                OrganizationPermissionAuditResponseSchema
            ),

        grantPermission: (data: GrantPermissionRequest) =>
            requestWithSchema<void>('/portal/v1/permissions/grant', VoidResponseSchema, {
                method: 'POST',
                body: GrantPermissionRequestSchema.parse(data),
            }),

        revokePermission: (data: RevokePermissionRequest) =>
            requestWithSchema<void>('/portal/v1/permissions/revoke', VoidResponseSchema, {
                method: 'POST',
                body: RevokePermissionRequestSchema.parse(data),
            }),

        repairAuditedPermission: (data: RepairAuditedPermissionRequest) =>
            requestWithSchema<void>('/portal/v1/permissions/audit/repair', VoidResponseSchema, {
                method: 'POST',
                body: RepairAuditedPermissionRequestSchema.parse(data),
            }),

        listSharedBundles: () =>
            requestWithSchema<ListPublicBundlesResponse>(
                '/portal/v1/permissions/bundles/shared',
                ListPublicBundlesResponseSchema
            ),

        searchSharedBundles: (params: SharedPermissionBundlesSearchRequest = {}) => {
            const requestBody: SharedPermissionBundlesSearchRequest =
                SharedPermissionBundlesSearchRequestSchema.parse(params);
            return requestWithSchema<SharedPermissionBundlesSearchPageResponse>(
                '/portal/v1/permissions/bundles/shared/search',
                SharedPermissionBundlesSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getBundle: (bundleUuid: string) =>
            requestWithSchema<PermissionsBundlePublic>(
                `/portal/v1/permissions/bundles/${bundleUuid}`,
                PermissionsBundlePublicSchema
            ),

        createSharedBundle: (data: CreateSharedBundleRequest) =>
            requestWithSchema<PermissionsBundlePublic>(
                '/portal/v1/permissions/bundles/shared',
                PermissionsBundlePublicSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        updateBundle: (bundleUuid: string, data: UpdateBundleRequest) =>
            requestWithSchema<PermissionsBundlePublic>(
                `/portal/v1/permissions/bundles/${bundleUuid}`,
                PermissionsBundlePublicSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            ),

        deleteBundle: (bundleUuid: string) =>
            requestWithSchema<void>(
                `/portal/v1/permissions/bundles/${bundleUuid}`,
                VoidResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        assignBundle: (data: AssignBundleRequest) =>
            requestWithSchema<void>('/portal/v1/permissions/assign-bundle', VoidResponseSchema, {
                method: 'POST',
                body: AssignBundleRequestSchema.parse(data),
            }),

        searchPolicyPermissionBundlesForOrganization: (
            organizationUuid: string,
            params: PolicyPermissionBundlesSearchRequest = {}
        ) => {
            const requestBody: PolicyPermissionBundlesSearchRequest =
                PolicyPermissionBundlesSearchRequestSchema.parse(params);
            return requestWithSchema<PolicyPermissionBundlesSearchPageResponse>(
                `/portal/v1/permissions/policy-bundles/organization/${organizationUuid}/search`,
                PolicyPermissionBundlesSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getPolicyPermissionBundle: (permissionsBundleUuid: string) =>
            requestWithSchema<PermissionsBundlePublic>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}`,
                PermissionsBundlePublicSchema
            ),

        createPolicyPermissionBundleForOrganization: (
            organizationUuid: string,
            data: CreatePolicyPermissionBundleRequest
        ) =>
            requestWithSchema<PermissionsBundlePublic>(
                `/portal/v1/permissions/policy-bundles/organization/${organizationUuid}`,
                PermissionsBundlePublicSchema,
                {
                    method: 'POST',
                    body: data,
                }
            ),

        updatePolicyPermissionBundle: (
            permissionsBundleUuid: string,
            data: UpdatePolicyPermissionBundleRequest
        ) =>
            requestWithSchema<PermissionsBundlePublic>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}`,
                PermissionsBundlePublicSchema,
                {
                    method: 'PATCH',
                    body: data,
                }
            ),

        deletePolicyPermissionBundle: (permissionsBundleUuid: string) =>
            requestWithSchema<void>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}`,
                VoidResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        listPolicyPermissionGrantsForRoleAssignment: (roleAssignmentUuid: string) =>
            requestWithSchema<ListPolicyPermissionGrantsResponse>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}`,
                ListPolicyPermissionGrantsResponseSchema
            ),

        grantPolicyPermissionBundleToRoleAssignment: (
            roleAssignmentUuid: string,
            data: GrantPolicyPermissionBundleRequest
        ) => {
            const parsedData: GrantPolicyPermissionBundleRequest =
                GrantPolicyPermissionBundleRequestSchema.parse(data);
            return requestWithSchema<void>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}`,
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: parsedData,
                }
            );
        },

        reorderPolicyPermissionGrantsForRoleAssignment: (
            roleAssignmentUuid: string,
            data: ReorderPolicyPermissionGrantsRequest
        ) => {
            const parsedData: ReorderPolicyPermissionGrantsRequest =
                ReorderPolicyPermissionGrantsRequestSchema.parse(data);
            return requestWithSchema<void>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}/reorder`,
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: parsedData,
                }
            );
        },

        revokePolicyPermissionBundleFromRoleAssignment: (
            roleAssignmentUuid: string,
            permissionsBundleUuid: string
        ) =>
            requestWithSchema<void>(
                `/portal/v1/permissions/policy-grants/role-assignment/${roleAssignmentUuid}/${permissionsBundleUuid}`,
                VoidResponseSchema,
                {
                    method: 'DELETE',
                }
            ),

        getOrganizationScopedPermissionSourceOptions: (organizationUuid: string) =>
            requestWithSchema<OrganizationScopedPermissionSourceOptionsResponse>(
                `/portal/v1/permissions/source-designators/organization/${organizationUuid}/options`,
                OrganizationScopedPermissionSourceOptionsResponseSchema
            ),

        searchRoleAssignmentsForOrganization: (
            organizationUuid: string,
            params: PermissionSourceRoleAssignmentsSearchRequest = {}
        ) => {
            const requestBody: PermissionSourceRoleAssignmentsSearchRequest =
                PermissionSourceRoleAssignmentsSearchRequestSchema.parse(params);
            return requestWithSchema<PermissionSourceRoleAssignmentsSearchPageResponse>(
                `/portal/v1/permissions/source-designators/organization/${organizationUuid}/role-assignments/search`,
                PermissionSourceRoleAssignmentsSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        searchRoleAssignmentsForPolicyPermissionBundle: (
            organizationUuid: string,
            permissionsBundleUuid: string,
            params: PolicyPermissionBundleRoleAssignmentsSearchRequest = {}
        ) => {
            const requestBody: PolicyPermissionBundleRoleAssignmentsSearchRequest =
                PolicyPermissionBundleRoleAssignmentsSearchRequestSchema.parse(params);
            return requestWithSchema<PolicyPermissionBundleRoleAssignmentsSearchPageResponse>(
                `/portal/v1/permissions/policy-bundles/${permissionsBundleUuid}/organization/${organizationUuid}/role-assignments/search`,
                PolicyPermissionBundleRoleAssignmentsSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getRoleEligibility: (role: string) =>
            requestWithSchema<RoleEligibility>(
                `/portal/v1/permissions/eligibility/${role}`,
                RoleEligibilitySchema
            ),
    };

    const authorizationApi = {
        canCreateUser: (organizationUuid: string) => {
            const query = CanCreateUserQuerySchema.parse({
                organization_uuid: organizationUuid,
            });
            const queryParams: URLSearchParams = new URLSearchParams({
                organization_uuid: query.organization_uuid,
            });
            return requestWithSchema(
                `/portal/v1/authorization/can_create_user?${queryParams.toString()}`,
                CanCreateUserResponseSchema
            );
        },

        canModifyUser: (userUuid: string) => {
            const query = CanModifyUserQuerySchema.parse({ user_uuid: userUuid });
            const queryParams: URLSearchParams = new URLSearchParams({
                user_uuid: query.user_uuid,
            });
            return requestWithSchema(
                `/portal/v1/authorization/can_modify_user?${queryParams.toString()}`,
                CanModifyUserResponseSchema
            );
        },

        canDeleteUser: (userUuid: string) => {
            const query = CanDeleteUserQuerySchema.parse({ user_uuid: userUuid });
            const queryParams: URLSearchParams = new URLSearchParams({
                user_uuid: query.user_uuid,
            });
            return requestWithSchema(
                `/portal/v1/authorization/can_delete_user?${queryParams.toString()}`,
                CanDeleteUserResponseSchema
            );
        },

        canImpersonateRoleAssignment: (roleAssignmentUuid: string) => {
            const validatedRoleAssignmentUuid: string = z.string().uuid().parse(roleAssignmentUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                role_assignment_uuid: validatedRoleAssignmentUuid,
            });
            return requestWithSchema(
                `/portal/v1/authorization/can_impersonate_role_assignment?${queryParams.toString()}`,
                CanImpersonateRoleAssignmentResponseSchema
            );
        },

        hasAccessToOrganization: (organizationUuid: string) => {
            const query = HasAccessToOrganizationQuerySchema.parse({
                organization_uuid: organizationUuid,
            });
            const queryParams: URLSearchParams = new URLSearchParams({
                organization_uuid: query.organization_uuid,
            });
            return requestWithSchema(
                `/portal/v1/authorization/has_access_to_organization?${queryParams.toString()}`,
                HasAccessToOrganizationResponseSchema
            );
        },

        hasAccessToUser: (userUuid: string) => {
            const validatedUserUuid: string = z.string().uuid().parse(userUuid);
            const queryParams: URLSearchParams = new URLSearchParams({
                user_uuid: validatedUserUuid,
            });
            return requestWithSchema(
                `/portal/v1/authorization/has_access_to_user?${queryParams.toString()}`,
                HasAccessToUserResponseSchema
            );
        },

        getAccessibleOrganizationIds: (params?: AccessibleOrganizationIdsRequest) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_organization_ids?${queryString}`
                    : '/portal/v1/authorization/accessible_organization_ids';
            return requestWithSchema(url, AccessibleOrganizationIdsResponseSchema);
        },

        getAccessibleOrganizations: (params?: AccessibleOrganizationIdsRequest) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor.toString());
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_organizations?${queryString}`
                    : '/portal/v1/authorization/accessible_organizations';
            return requestWithSchema(url, AccessibleOrganizationsResponseSchema);
        },

        getAccessibleUserIds: (params?: AccessibleUserIdsRequestNoPkey) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            if (params?.organization_uuid !== undefined) {
                const validatedOrganizationUuid: string = z
                    .string()
                    .uuid()
                    .parse(params.organization_uuid);
                queryParams.set('organization_uuid', validatedOrganizationUuid);
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_user_ids?${queryString}`
                    : '/portal/v1/authorization/accessible_user_ids';
            return requestWithSchema(url, AccessibleUserIdsResponseSchema);
        },

        getAccessibleUsers: (params?: AccessibleUserIdsRequestNoPkey) => {
            const queryParams = new URLSearchParams();
            if (params?.limit !== undefined) {
                queryParams.set('limit', params.limit.toString());
            }
            if (params?.cursor !== undefined) {
                queryParams.set('cursor', params.cursor);
            }
            if (params?.organization_uuid !== undefined) {
                const validatedOrganizationUuid: string = z
                    .string()
                    .uuid()
                    .parse(params.organization_uuid);
                queryParams.set('organization_uuid', validatedOrganizationUuid);
            }
            const queryString: string = queryParams.toString();
            const url: string =
                queryString.length > 0
                    ? `/portal/v1/authorization/accessible_users?${queryString}`
                    : '/portal/v1/authorization/accessible_users';
            return requestWithSchema(url, AccessibleUsersResponseSchema);
        },

        isAuthorized: (params: IsAuthorizedRequest) => {
            return requestWithSchema(
                '/portal/v1/authorization/is_authorized',
                IsAuthorizedResponseSchema,
                {
                    method: 'POST',
                    body: IsAuthorizedRequestSchema.parse(params),
                }
            );
        },
    };

    const legalDocumentsApi = {
        searchLegalDocumentsPage: (params: LegalDocumentsSearchRequest = {}) => {
            const requestBody: LegalDocumentsSearchRequest =
                LegalDocumentsSearchRequestSchema.parse(params);
            return requestWithSchema<SearchLegalDocumentsPageResponse>(
                '/portal/v1/legal_documents/search',
                SearchLegalDocumentsPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        createLegalDocument: (data: CreateLegalDocumentRequest) => {
            const parseResult = CreateLegalDocumentRequestSchema.safeParse(data);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid create legal document request: ${parseResult.error.message}`
                );
            }
            return requestWithSchema<CreateLegalDocumentResponse>(
                '/portal/v1/legal_documents',
                CreateLegalDocumentResponseSchema,
                {
                    method: 'POST',
                    body: parseResult.data,
                }
            );
        },

        getLegalDocument: (legalDocumentUuid: string) => {
            const idParseResult = LegalDocumentUuidParamSchema.safeParse(legalDocumentUuid);
            if (!idParseResult.success) {
                throw new Error(`Invalid legal document UUID: ${idParseResult.error.message}`);
            }
            return requestWithSchema<GetLegalDocumentResponse>(
                `/portal/v1/legal_documents/${idParseResult.data}`,
                GetLegalDocumentResponseSchema
            );
        },

        listPendingRequiredLegalDocuments: dedupe(() =>
            requestWithSchema<ListPendingRequiredLegalDocumentsResponse>(
                '/portal/v1/legal_documents/pending',
                ListPendingRequiredLegalDocumentsResponseSchema
            )
        ),

        acceptLegalDocument: (legalDocumentUuid: string, data: AcceptLegalDocumentRequest) => {
            const idParseResult = LegalDocumentUuidParamSchema.safeParse(legalDocumentUuid);
            if (!idParseResult.success) {
                throw new Error(`Invalid legal document UUID: ${idParseResult.error.message}`);
            }
            const parseResult = AcceptLegalDocumentRequestSchema.safeParse(data);
            if (!parseResult.success) {
                throw new Error(
                    `Invalid accept legal document request: ${parseResult.error.message}`
                );
            }
            return requestWithSchema<void>(
                `/portal/v1/legal_documents/${idParseResult.data}/accept`,
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: parseResult.data,
                }
            );
        },

        updateLegalDocumentStatus: async (
            legalDocumentUuid: string,
            data: UpdateLegalDocumentStatusRequest
        ) => {
            const idParseResult = LegalDocumentUuidParamSchema.safeParse(legalDocumentUuid);
            if (!idParseResult.success) {
                throw new Error(`Invalid legal document UUID: ${idParseResult.error.message}`);
            }
            const status: unknown = data.status;
            if (status !== 'ACTIVE' && status !== 'DISABLED') {
                throw new Error(
                    'Invalid legal document status update request: status must be ACTIVE or DISABLED'
                );
            }
            return await requestWithSchema<GetLegalDocumentResponse>(
                `/portal/v1/legal_documents/${idParseResult.data}`,
                GetLegalDocumentResponseSchema,
                {
                    method: 'PATCH',
                    body: { status },
                }
            );
        },
    };

    const auditApi = {
        listTransactionLogPage: (params: TransactionLogSearchRequest = {}) => {
            const requestBody: TransactionLogSearchRequest =
                TransactionLogSearchRequestSchema.parse(params);
            return requestWithSchema<ListTransactionLogPageResponse>(
                '/portal/v1/audit/transaction_log/search',
                ListTransactionLogPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },
        listAuthorOptionsPage: (params: ForensicOptionSearchRequest = {}) => {
            const requestBody = ForensicOptionSearchRequestSchema.parse(params);
            return requestWithSchema<ListForensicReferenceOptionsPageResponse>(
                '/portal/v1/audit/options/authors/search',
                ListForensicReferenceOptionsPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
        listOrganizationOptionsPage: (params: ForensicOptionSearchRequest = {}) => {
            const requestBody = ForensicOptionSearchRequestSchema.parse(params);
            return requestWithSchema<ListForensicReferenceOptionsPageResponse>(
                '/portal/v1/audit/options/organizations/search',
                ListForensicReferenceOptionsPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
        listSessionOptionsPage: (params: ForensicOptionSearchRequest = {}) => {
            const requestBody = ForensicOptionSearchRequestSchema.parse(params);
            return requestWithSchema<ListForensicSessionOptionsPageResponse>(
                '/portal/v1/audit/options/sessions/search',
                ListForensicSessionOptionsPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
        listRecentUserActivityPage: (params: RecentUserActivitySearchRequest = {}) => {
            const requestBody = RecentUserActivitySearchRequestSchema.parse(params);
            return requestWithSchema<ListRecentUserActivityPageResponse>(
                '/portal/v1/audit/recent-activity/users/search',
                ListRecentUserActivityPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
        listRecentRoleActivityPage: (params: RecentRoleActivitySearchRequest = {}) => {
            const requestBody = RecentRoleActivitySearchRequestSchema.parse(params);
            return requestWithSchema<ListRecentRoleActivityPageResponse>(
                '/portal/v1/audit/recent-activity/roles/search',
                ListRecentRoleActivityPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
    };

    const operationalApi = {
        getHealth: (headers?: HttpRequestHeaders) =>
            requestWithSchema<HealthResponse>('/health', HealthResponseSchema, {
                headers,
                handle401AsSessionExpired: false,
                acceptedStatusCodes: [503],
            }),
    };

    const securityApi = {
        reportCspViolation: (report: CspViolationReportPayload, headers?: HttpRequestHeaders) => {
            const requestBody: CspViolationReportPayload = CspViolationReportSchema.parse(report);
            return requestWithSchema<void>('/portal/v1/security/csp-violation', VoidResponseSchema, {
                method: 'POST',
                body: requestBody,
                headers,
                handle401AsSessionExpired: false,
            });
        },
    };

    const paymentWebhookApi = {
        postTwilioStatusCallback: (
            payload: TwilioStatusCallbackPayload,
            headers?: HttpRequestHeaders
        ) => {
            const requestBody: TwilioStatusCallbackPayload =
                TwilioStatusCallbackPayloadSchema.parse(payload);
            return requestWithSchema<void>(
                '/portal/v1/payments/webhooks/twilio/status-callback',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                    headers,
                    handle401AsSessionExpired: false,
                }
            );
        },

        postSendgridEventWebhook: (
            payload: SendgridEventWebhookPayload,
            headers?: HttpRequestHeaders
        ) => {
            const requestBody: SendgridEventWebhookPayload =
                SendgridEventWebhookPayloadSchema.parse(payload);
            return requestWithSchema<void>(
                '/portal/v1/payments/webhooks/sendgrid/event-webhook',
                VoidResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                    headers,
                    handle401AsSessionExpired: false,
                }
            );
        },
    };

    const allowedWebsiteTemplatesApi = {
        searchTemplates: (params: AllowedWebsiteTemplateSearchRequest = {}) => {
            const requestBody: AllowedWebsiteTemplateSearchRequest =
                AllowedWebsiteTemplateSearchRequestSchema.parse(params);
            return requestWithSchema<AllowedWebsiteTemplateSearchPageResponse>(
                '/portal/v1/allowed_website_templates/search',
                AllowedWebsiteTemplateSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        getTemplate: (templateUuid: string) =>
            requestWithSchema<AllowedWebsiteTemplateMutationResponse>(
                buildAllowedWebsiteTemplateEndpoint(templateUuid),
                AllowedWebsiteTemplateMutationResponseSchema
            ),

        createTemplate: dedupe((data: CreateAllowedWebsiteTemplateRequest) => {
            const requestBody: CreateAllowedWebsiteTemplateRequest =
                CreateAllowedWebsiteTemplateRequestSchema.parse(data);
            return requestWithSchema<AllowedWebsiteTemplateMutationResponse>(
                '/portal/v1/allowed_website_templates',
                AllowedWebsiteTemplateMutationResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        }),

        updateTemplate: dedupe(
            (templateUuid: string, data: UpdateAllowedWebsiteTemplateRequest) => {
                const requestBody: UpdateAllowedWebsiteTemplateRequest =
                    UpdateAllowedWebsiteTemplateRequestSchema.parse(data);
                return requestWithSchema<AllowedWebsiteTemplateMutationResponse>(
                    buildAllowedWebsiteTemplateEndpoint(templateUuid),
                    AllowedWebsiteTemplateMutationResponseSchema,
                    {
                        method: 'PATCH',
                        body: requestBody,
                    }
                );
            }
        ),

        enableTemplate: dedupe((data: AllowedWebsiteTemplateUuidRequest) => {
            const requestData: AllowedWebsiteTemplateUuidRequest =
                AllowedWebsiteTemplateUuidRequestSchema.parse(data);
            return requestWithSchema<AllowedWebsiteTemplateMutationResponse>(
                buildAllowedWebsiteTemplateEndpoint(
                    requestData.allowed_website_template_uuid,
                    '/enable'
                ),
                AllowedWebsiteTemplateMutationResponseSchema,
                {
                    method: 'POST',
                }
            );
        }),

        disableTemplate: dedupe((data: AllowedWebsiteTemplateUuidRequest) => {
            const requestData: AllowedWebsiteTemplateUuidRequest =
                AllowedWebsiteTemplateUuidRequestSchema.parse(data);
            return requestWithSchema<AllowedWebsiteTemplateMutationResponse>(
                buildAllowedWebsiteTemplateEndpoint(
                    requestData.allowed_website_template_uuid,
                    '/disable'
                ),
                AllowedWebsiteTemplateMutationResponseSchema,
                {
                    method: 'POST',
                }
            );
        }),

        deleteTemplate: dedupe((data: AllowedWebsiteTemplateUuidRequest) => {
            const requestData: AllowedWebsiteTemplateUuidRequest =
                AllowedWebsiteTemplateUuidRequestSchema.parse(data);
            return requestWithSchema<SafeDeleteAllowedWebsiteTemplateResponse>(
                buildAllowedWebsiteTemplateEndpoint(requestData.allowed_website_template_uuid),
                SafeDeleteAllowedWebsiteTemplateResponseSchema,
                {
                    method: 'DELETE',
                }
            );
        }),

        getBlastRadius: (templateUuid: string) =>
            requestWithSchema<AllowedWebsiteTemplateBlastRadiusResponse>(
                buildAllowedWebsiteTemplateEndpoint(templateUuid, '/blast_radius'),
                AllowedWebsiteTemplateBlastRadiusResponseSchema
            ),

        listOrganizationWebsites: (organizationUuid: string) =>
            requestWithSchema<ListOrganizationAllowedWebsitesResponse>(
                buildOrganizationAllowedWebsitesEndpoint(organizationUuid),
                ListOrganizationAllowedWebsitesResponseSchema
            ),

        searchUsableTemplatesForOrganization: (
            organizationUuid: string,
            params: AllowedWebsiteTemplateSearchRequest = {}
        ) => {
            const requestBody: AllowedWebsiteTemplateSearchRequest =
                AllowedWebsiteTemplateSearchRequestSchema.parse(params);
            return requestWithSchema<AllowedWebsiteTemplateSearchPageResponse>(
                buildOrganizationAllowedWebsitesEndpoint(
                    organizationUuid,
                    '/usable_templates/search'
                ),
                AllowedWebsiteTemplateSearchPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },

        createLocalOrganizationWebsite: dedupe(
            (organizationUuid: string, data: CreateLocalOrganizationAllowedWebsiteRequest) => {
                const requestBody: CreateLocalOrganizationAllowedWebsiteRequest =
                    CreateLocalOrganizationAllowedWebsiteRequestSchema.parse(data);
                return requestWithSchema<OrganizationAllowedWebsiteMutationResponse>(
                    buildOrganizationAllowedWebsitesEndpoint(organizationUuid, '/local'),
                    OrganizationAllowedWebsiteMutationResponseSchema,
                    {
                        method: 'POST',
                        body: requestBody,
                    }
                );
            }
        ),

        referenceTemplateForOrganization: dedupe(
            (
                organizationUuid: string,
                data: ReferenceAllowedWebsiteTemplateToOrganizationRequest
            ) => {
                const requestBody: ReferenceAllowedWebsiteTemplateToOrganizationRequest =
                    ReferenceAllowedWebsiteTemplateToOrganizationRequestSchema.parse(data);
                return requestWithSchema<OrganizationAllowedWebsiteMutationResponse>(
                    buildOrganizationAllowedWebsitesEndpoint(
                        organizationUuid,
                        '/template_reference'
                    ),
                    OrganizationAllowedWebsiteMutationResponseSchema,
                    {
                        method: 'POST',
                        body: requestBody,
                    }
                );
            }
        ),

        updateOrganizationWebsite: dedupe(
            (
                organizationUuid: string,
                organizationAllowedWebsiteUuid: string,
                data: UpdateOrganizationAllowedWebsiteRequest
            ) => {
                const requestBody: UpdateOrganizationAllowedWebsiteRequest =
                    UpdateOrganizationAllowedWebsiteRequestSchema.parse(data);
                return requestWithSchema<OrganizationAllowedWebsiteMutationResponse>(
                    buildOrganizationAllowedWebsiteEndpoint(
                        organizationUuid,
                        organizationAllowedWebsiteUuid
                    ),
                    OrganizationAllowedWebsiteMutationResponseSchema,
                    {
                        method: 'PATCH',
                        body: requestBody,
                    }
                );
            }
        ),

        deleteOrganizationWebsite: dedupe(
            (organizationUuid: string, organizationAllowedWebsiteUuid: string) =>
                requestWithSchema<DeleteOrganizationAllowedWebsiteResponse>(
                    buildOrganizationAllowedWebsiteEndpoint(
                        organizationUuid,
                        organizationAllowedWebsiteUuid
                    ),
                    DeleteOrganizationAllowedWebsiteResponseSchema,
                    {
                        method: 'DELETE',
                    }
                )
        ),

        customizeTemplateReferenceForOrganization: dedupe(
            (organizationUuid: string, organizationAllowedWebsiteUuid: string) =>
                requestWithSchema<OrganizationAllowedWebsiteMutationResponse>(
                    buildOrganizationAllowedWebsiteEndpoint(
                        organizationUuid,
                        organizationAllowedWebsiteUuid,
                        '/customize_template'
                    ),
                    OrganizationAllowedWebsiteMutationResponseSchema,
                    {
                        method: 'POST',
                    }
                )
        ),

        promoteOrganizationWebsiteToTemplate: dedupe(
            (organizationUuid: string, data: PromoteOrganizationWebsiteToTemplateRequest) => {
                const requestBody: PromoteOrganizationWebsiteToTemplateRequest =
                    PromoteOrganizationWebsiteToTemplateRequestSchema.parse(data);
                return requestWithSchema<PromoteOrganizationWebsiteToTemplateResponse>(
                    buildOrganizationAllowedWebsiteEndpoint(
                        organizationUuid,
                        requestBody.organization_allowed_website_uuid,
                        '/promote_to_template'
                    ),
                    PromoteOrganizationWebsiteToTemplateResponseSchema,
                    {
                        method: 'POST',
                        body: requestBody,
                    }
                );
            }
        ),
    };

    const loggingApi = {
        ingestLogs: (data: LogIngestRequest) => {
            const requestBody: LogIngestRequest = LogIngestRequestSchema.parse(data);
            return requestWithSchema<void>('/portal/v1/logs', VoidResponseSchema, {
                method: 'POST',
                body: requestBody,
            });
        },
        listCloudLoggingEntriesPage: (data: CloudLoggingQueryRequest) => {
            const requestBody: CloudLoggingQueryRequest =
                CloudLoggingQueryRequestSchema.parse(data);
            return requestWithSchema<ListCloudLoggingEntriesPageResponse>(
                '/portal/v1/logs/search',
                ListCloudLoggingEntriesPageResponseSchema,
                {
                    method: 'POST',
                    body: requestBody,
                }
            );
        },
        listCloudLoggingLogIdOptionsPage: (data: CloudLoggingOptionSearchRequest = {}) => {
            const requestBody: CloudLoggingOptionSearchRequest =
                CloudLoggingOptionSearchRequestSchema.parse(data);
            return requestWithSchema<ListCloudLoggingOptionsPageResponse>(
                '/portal/v1/logs/options/log-names/search',
                ListCloudLoggingOptionsPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
        listCloudLoggingResourceTypeOptionsPage: (data: CloudLoggingOptionSearchRequest = {}) => {
            const requestBody: CloudLoggingOptionSearchRequest =
                CloudLoggingOptionSearchRequestSchema.parse(data);
            return requestWithSchema<ListCloudLoggingOptionsPageResponse>(
                '/portal/v1/logs/options/resource-types/search',
                ListCloudLoggingOptionsPageResponseSchema,
                { method: 'POST', body: requestBody }
            );
        },
    };

    const appApi = {};

    return {
        baseUrl,
        requestWithSchema,
        authApi,
        userApi,
        organizationApi,
        customerApi,
        paymentsInfrastructureApi,
        sambaConfigApi,
        paymentsApi,
        sambaApi,
        diagnosticsApi,
        encryptionKeysApi,
        secretsApi,
        rpaCredentialInjectionApi,
        rbsIpsWorkflowRuntimeApi,
        ipxCredentialManagementApi,
        rpaBuilderApi,
        permissionsApi,
        allowedWebsiteTemplatesApi,
        authorizationApi,
        legalDocumentsApi,
        auditApi,
        operationalApi,
        securityApi,
        paymentWebhookApi,
        loggingApi,
        appApi,
    };
}

export type PillarApiClient = ReturnType<typeof createPillarApiWithOptions>;
